<template>
  <div class="user-agreement-editor">
    <button class="back-link" type="button" @click="goBack">
      &larr; Back to Agreements
    </button>

    <CCard class="editor-shell border-0 shadow-sm">
      <CCardBody class="p-4 p-lg-5">
        <div class="page-header">
          <div>
            <div class="kicker">Agreement Draft</div>
            <h2>{{ agreementType }}</h2>
            <p>Choose the draft method that fits this agreement, add the sections you want directly into the draft, and review the live preview before continuing.</p>
          </div>
          <div class="header-actions" v-if="!isViewMode">
            <CButton color="secondary" variant="outline" @click="saveDraft">Save Draft</CButton>
            <CButton color="primary" @click="goNext">Continue</CButton>
          </div>
        </div>

        <div v-if="error" class="notice notice--error">{{ error }}</div>
        <div v-if="success" class="notice notice--success">{{ success }}</div>
        <div class="layout-grid">
          <div class="editor-column">
            <div class="mode-tabs" v-if="!isViewMode">
              <button
                v-for="mode in modeOptions"
                :key="mode.value"
                type="button"
                class="mode-tab"
                :class="{ active: form.draftSource === mode.value }"
                @click="setDraftSource(mode.value)"
              >
                {{ mode.label }}
              </button>
            </div>

            <div class="form-grid">
              <label class="field">
                <span>Agreement Title</span>
                <input v-model="form.title" :disabled="isViewMode" />
              </label>
              <label class="field">
                <span>Partner Institution</span>
                <input v-model="form.partner" :disabled="isViewMode" />
              </label>
              <div class="field">
                <div class="field-heading field-heading--compact">
                  <span>Countries</span>
                </div>
                <div
                  ref="countryPicker"
                  class="country-picker"
                  :class="{ 'country-picker--disabled': isViewMode }"
                  @focusin="openCountryPicker"
                  @focusout="handleCountryPickerBlur"
                >
                  <div class="country-picker__control">
                    <span
                      v-for="country in selectedCountryValues"
                      :key="country"
                      class="country-chip"
                    >
                      {{ country }}
                      <button
                        v-if="!isViewMode"
                        type="button"
                        class="country-chip__remove"
                        :aria-label="`Remove ${country}`"
                        @click.stop="removeCountry(country)"
                      >
                        <CIcon name="cil-x" />
                      </button>
                    </span>
                    <input
                      v-if="!isViewMode"
                      v-model="countrySearch"
                      type="search"
                      class="country-picker__input"
                      :placeholder="selectedCountryValues.length ? '' : countryInputPlaceholder"
                      @keydown.enter.prevent="selectFirstFilteredCountry"
                      @keydown.backspace="removeLastCountryIfSearchEmpty"
                    >
                    <span v-else-if="!selectedCountryValues.length" class="country-picker__placeholder">
                      Select one or more countries
                    </span>
                  </div>
                  <div
                    v-if="!isViewMode && countryPickerOpen"
                    class="country-picker__menu"
                  >
                    <button
                      v-for="country in filteredCountryOptions"
                      :key="country"
                      type="button"
                      class="country-picker__option"
                      @mousedown.prevent="addCountry(country)"
                    >
                      {{ country }}
                    </button>
                    <div v-if="!filteredCountryOptions.length" class="country-picker__empty">
                      No countries found
                    </div>
                  </div>
                </div>
              </div>
              <label class="field">
                <span>Partner Email</span>
                <input v-model="form.partnerEmail" type="email" :disabled="isViewMode" />
              </label>
              <label class="field">
                <span>Contact Person</span>
                <input v-model="form.contactPerson" :disabled="isViewMode" />
              </label>
              <label class="field">
                <span>Department</span>
                <input v-model="form.department" :disabled="isViewMode" />
              </label>
              <label class="field">
                <span>Partner Logo</span>
                <template v-if="!isViewMode">
                  <input type="file" accept="image/png,image/jpeg" @change="handlePartnerLogoUpload">
                  <small class="field-help">Accepted files: PNG, JPG, or JPEG.</small>
                </template>
                <div v-else class="logo-input-note">{{ partnerLogoName }}</div>
              </label>
              <label class="field field--full">
                <div class="field-heading">
                  <span>Additional Note</span>
                  <button
                    v-if="!isViewMode"
                    type="button"
                    class="grammar-chip"
                    :class="`grammar-chip--${grammarChipTone('summary', form.summary)}`"
                    :aria-pressed="grammarWorkspaceVisible && activeGrammarFieldKey === 'summary'"
                    :disabled="grammarButtonDisabled('summary', form.summary)"
                    @click="runSummaryGrammarCheck"
                  >
                    <span class="grammar-chip__dot"></span>
                    <span class="grammar-chip__label">{{ grammarChipLabel('summary', form.summary) }}</span>
                    <span v-if="grammarChipBadge('summary', form.summary)" class="grammar-chip__badge">
                      {{ grammarChipBadge('summary', form.summary) }}
                    </span>
                  </button>
                </div>
                <textarea v-model="form.summary" rows="3" :disabled="isViewMode" />
              </label>
            </div>

            <div class="content-card" v-if="form.draftSource !== 'upload' || hasImportedEditableSections">
              <div class="content-card__header">
                <div>
                  <div class="content-card__title">{{ form.draftSource === 'upload' ? 'Imported Contract Sections' : 'Draft Builder' }}</div>
                  <p class="content-card__copy">
                    {{ form.draftSource === 'upload'
                      ? 'Your uploaded contract file and any selected clause library items are shown here as editable website sections before continuing.'
                      : 'The agreement articles are prefilled from the real template and can be edited directly. Add your own articles anywhere you need.' }}
                  </p>
                </div>
                <div v-if="!isViewMode" class="content-card__actions">
                  <CButton v-if="form.draftSource !== 'upload'" color="secondary" variant="outline" size="sm" @click="resetArticlesFromTemplate">Reset Template</CButton>
                  <CButton color="primary" size="sm" @click="addArticle">Add Article</CButton>
                  <CButton color="secondary" variant="outline" size="sm" @click="addSignatureBlock">Add Signature Block</CButton>
                </div>
              </div>

              <div class="article-list">
                <div 
                  v-for="(article, index) in pagedArticles" 
                  :key="article.key || index" 
                  class="article-card"
                  :class="{ 'article-card--dragging': draggedIndex === pageStartIndex + index }"
                  :draggable="!isViewMode && form.articleDrafts.length > 1"
                  @dragstart="startDragArticle($event, pageStartIndex + index)"
                  @dragover="dragOverArticle($event, pageStartIndex + index)"
                  @dragleave="dragLeaveArticle"
                  @drop="dropArticle($event, pageStartIndex + index)"
                  @dragend="endDragArticle"
                >
                  <div class="article-card__meta">
                    <div class="article-drag-handle" v-if="!isViewMode && form.articleDrafts.length > 1" title="Drag to reorder">
                      <span class="drag-icon">⋮⋮</span>
                    </div>
                    <span class="article-chip">{{ articleChipLabel(article, pageStartIndex + index) }}</span>
                    <span v-if="isClauseArticle(article)" class="article-source-chip">Clause Library</span>
                    <div v-if="!isViewMode && form.articleDrafts.length > 1" class="article-card__controls">
                      <button
                        type="button"
                        class="article-move-trigger"
                        :class="{ 'article-move-trigger--active': activeMoveArticleIndex === pageStartIndex + index }"
                        @click="toggleArticleMovePanel(pageStartIndex + index)"
                      >
                        Move
                      </button>
                      <button
                        type="button"
                        class="article-remove"
                        @click="removeArticle(pageStartIndex + index)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="!isViewMode && form.articleDrafts.length > 1 && activeMoveArticleIndex === pageStartIndex + index"
                    class="article-move-panel"
                  >
                    <label class="field field--full article-move-panel__field">
                      <span>Move to</span>
                      <select
                        :value="pageStartIndex + index"
                        @change="moveArticle(pageStartIndex + index, $event)"
                      >
                        <option
                          v-for="option in articleMoveDestinationOptions()"
                          :key="`move-position-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </label>
                  </div>
                  <label class="field">
                    <span>Article Title</span>
                    <input v-model="article.title" :disabled="isViewMode" />
                  </label>
                  <label class="field field--full">
                    <div class="field-heading">
                      <span>Article Content</span>
                      <button
                        v-if="!isViewMode"
                        type="button"
                        class="grammar-chip"
                        :class="`grammar-chip--${grammarChipTone(articleGrammarFieldKey(article, pageStartIndex + index), article.content)}`"
                        :aria-pressed="grammarWorkspaceVisible && activeGrammarFieldKey === articleGrammarFieldKey(article, pageStartIndex + index)"
                        :disabled="grammarButtonDisabled(articleGrammarFieldKey(article, pageStartIndex + index), article.content)"
                        @click="runArticleGrammarCheck(article, pageStartIndex + index)"
                      >
                        <span class="grammar-chip__dot"></span>
                        <span class="grammar-chip__label">{{ grammarChipLabel(articleGrammarFieldKey(article, pageStartIndex + index), article.content) }}</span>
                        <span
                          v-if="grammarChipBadge(articleGrammarFieldKey(article, pageStartIndex + index), article.content)"
                          class="grammar-chip__badge"
                        >
                          {{ grammarChipBadge(articleGrammarFieldKey(article, pageStartIndex + index), article.content) }}
                        </span>
                      </button>
                    </div>
                    <textarea v-model="article.content" rows="8" :disabled="isViewMode" />
                  </label>
                </div>
              </div>

              <div v-if="totalArticlePages > 1" class="article-pagination">
                <CButton
                  size="sm"
                  class="article-pagination__button article-pagination__button--secondary"
                  :disabled="articlePage === 1"
                  @click="articlePage -= 1"
                >
                  Previous
                </CButton>
                <span class="article-pagination__label">Page {{ articlePage }} of {{ totalArticlePages }}</span>
                <CButton
                  v-if="articlePage < totalArticlePages"
                  size="sm"
                  class="article-pagination__button article-pagination__button--primary"
                  @click="articlePage += 1"
                >
                  Next
                </CButton>
                <CButton
                  v-else
                  size="sm"
                  class="article-pagination__button article-pagination__button--primary"
                  @click="goNext"
                >
                  Continue
                </CButton>
              </div>
            </div>

            <div class="content-card" v-else>
              <div class="content-card__title">Import Contract</div>
              <p class="content-card__copy">Import a contract as a DOC or DOCX file. After you save the draft, the website will extract text into editable review sections for editing on the website.</p>
              <div class="upload-box">
                <input v-if="!isViewMode" type="file" :accept="acceptedContractTypes" @change="handleFileUpload">
                <div class="upload-meta" v-if="form.uploadedDraft && form.uploadedDraft.name">
                  <strong>{{ form.uploadedDraft.name }}</strong>
                  <span>{{ form.uploadedDraft.sizeLabel || 'Contract file uploaded' }}</span>
                </div>
                <div class="upload-meta" v-else>No contract imported yet.</div>
              </div>
            </div>

            <div class="content-card clause-library-card">
              <div class="content-card__header">
                <div>
                  <div class="content-card__title">Clause Library</div>
                  <p class="content-card__copy">
                    Select the clause library items you want embedded in this agreement. Chosen clauses are appended into the live preview and saved with the draft.
                  </p>
                </div>
                <div class="clause-header-stats">
                  <span class="clause-selection-pill">{{ selectedClauseCount }} Selected</span>
                  <span class="clause-stats-text">{{ filteredClauseOptions.length }} Available</span>
                </div>
              </div>

              <div v-if="availableClauses.length" class="clause-library-tools">
                <input
                  v-model="clauseSearchQuery"
                  type="search"
                  class="clause-library-search"
                  :disabled="isViewMode"
                  placeholder="Search clauses by title, category, or content"
                >
                <select v-model="activeClauseCategory" class="clause-library-filter" :disabled="isViewMode">
                  <option v-for="item in clauseCategoryOptions" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </option>
                </select>
              </div>

              <div v-if="availableClauses.length && !isViewMode" class="clause-quick-actions">
                <button 
                  type="button"
                  class="clause-quick-action-btn"
                  @click="selectAllVisibleClauses"
                  :disabled="filteredClauseOptions.length === 0"
                >
                  Select All Visible
                </button>
                <button 
                  type="button"
                  class="clause-quick-action-btn clause-quick-action-btn--secondary"
                  @click="clearAllSelectedClauses"
                  :disabled="selectedClauseCount === 0"
                >
                  Clear Selection
                </button>
              </div>

              <div v-if="filteredClauseOptions.length" class="clause-option-list">
                <label 
                  v-for="item in filteredClauseOptions" 
                  :key="item.id" 
                  class="clause-option-card"
                  :class="{ 'clause-option-card--selected': selectedClauseIds.includes(item.id) }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedClauseIds.includes(item.id)"
                    :disabled="isViewMode"
                    @change="toggleClauseSelection(item.id, $event.target.checked)"
                  >
                  <div class="clause-option-card__body">
                    <div class="clause-option-card__header">
                      <div class="clause-option-card__title">{{ item.title }}</div>
                      <div class="clause-option-card__badges">
                        <span class="clause-option-card__badge">{{ item.category || 'General' }}</span>
                        <span v-if="item.sourceType === 'default'" class="clause-option-card__badge clause-option-card__badge--muted">Starter</span>
                        <span v-else class="clause-option-card__badge clause-option-card__badge--muted">My Clause</span>
                        <span v-if="isSuggestedClause(item.id)" class="clause-option-card__badge clause-option-card__badge--suggested">Suggested</span>
                      </div>
                    </div>
                    <div 
                      class="clause-option-card__content"
                      :class="{ 'clause-option-card__content--collapsed': !expandedClauseIds.includes(item.id) }"
                      @click.stop="toggleClauseExpand(item.id)"
                    >
                      {{ item.content }}
                      <button 
                        v-if="shouldShowExpandButton(item.content)"
                        type="button"
                        class="clause-expand-toggle"
                        :class="{ 'clause-expand-toggle--expanded': expandedClauseIds.includes(item.id) }"
                        @click.stop="toggleClauseExpand(item.id)"
                        :disabled="isViewMode"
                      >
                        {{ expandedClauseIds.includes(item.id) ? 'Show Less' : 'Show More' }}
                      </button>
                    </div>
                  </div>
                </label>
              </div>

              <div v-else class="clause-library-empty">
                {{ hasSavedDraft ? 'No matching clauses were found for this agreement.' : 'The agreement draft is still being prepared.' }}
              </div>
            </div>
          </div>

          <div class="preview-column">
            <div class="preview-card">
              <div class="preview-card__header">
                <div>
                  <div class="preview-kicker">Live Preview</div>
                  <h3>{{ previewTitle }}</h3>
                </div>
                <span class="preview-pill">{{ draftModeLabel }}</span>
              </div>

              <AgreementDocumentHeader
                class="preview-card__document-header"
                :title="previewTitle"
                :partner="form.partner"
                :partner-logo="form.partnerLogo"
                :agreement-type="agreementType"
                :country="selectedCountryText"
                title-label="Agreement Title"
              />

              <div class="preview-meta">
                <span>{{ form.partner || 'Partner institution' }}</span>
                <span>{{ selectedCountryText || 'Country' }}</span>
                <span>{{ agreementType }}</span>
              </div>

              <div v-if="form.draftSource === 'upload'" class="import-preview">
                <div class="import-preview__info">
                  <strong>{{ form.uploadedDraft && form.uploadedDraft.name ? form.uploadedDraft.name : 'No file selected' }}</strong>
                  <span>{{ form.uploadedDraft && form.uploadedDraft.sizeLabel ? form.uploadedDraft.sizeLabel : 'Import a DOC or DOCX contract to turn it into editable website content.' }}</span>
                </div>
                <div v-if="uploadedDraftUrl" class="document-file-card">
                  <div class="document-file-card__title">Attached Contract File</div>
                  <a :href="uploadedDraftUrl" target="_blank" rel="noopener" class="document-file-card__link">Open file</a>
                </div>
                <div v-if="previewSections.length" class="document-preview">
                  <div
                    v-for="section in previewSections"
                    :key="section.key || section.label"
                    class="preview-section"
                  >
                    <div v-if="section.label" class="preview-section__label">{{ section.label }}</div>
                    <AgreementSignaturePreview
                      v-if="isSignaturePreviewSection(section)"
                      :section="section"
                      left-header="Mae Fah Luang University"
                      :right-header="[form.partner, selectedCountryText].filter(Boolean).join('\n')"
                    />
                    <p v-else :class="{ 'preview-section__content--centered': isCenteredPreviewSection(section) }">{{ section.content }}</p>
                  </div>
                </div>
                <div v-else class="empty-preview">
                  Save this imported draft once to generate the editable in-website preview.
                </div>
              </div>

              <div v-else class="document-preview">
                <div
                  v-for="section in previewSections"
                  :key="section.key || section.label"
                  class="preview-section"
                >
                  <div v-if="section.label" class="preview-section__label">{{ section.label }}</div>
                  <AgreementSignaturePreview
                    v-if="isSignaturePreviewSection(section)"
                    :section="section"
                    left-header="Mae Fah Luang University"
                    :right-header="[form.partner, selectedCountryText].filter(Boolean).join('\n')"
                  />
                  <p v-else :class="{ 'preview-section__content--centered': isCenteredPreviewSection(section) }">{{ section.content }}</p>
                </div>
                <div v-if="!previewSections.length" class="empty-preview">
                  Start filling out the draft to generate the preview.
                </div>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <transition name="grammar-workspace-fade">
      <aside
        v-if="!isViewMode && grammarWorkspaceVisible"
        class="grammar-workspace"
        :class="`grammar-workspace--${grammarPanelTone(activeGrammarFieldKey, activeGrammarFieldText)}`"
      >
        <div class="grammar-workspace__panel">
          <div class="grammar-workspace__header">
            <div class="grammar-workspace__intro">
              <div class="grammar-workspace__eyebrow">Grammar Assistant</div>
              <h3>{{ activeGrammarFieldLabel }}</h3>
              <p>English-only drafting help that keeps suggestions separate from the form while you write.</p>
            </div>
            <div class="grammar-workspace__actions">
              <button
                type="button"
                class="grammar-workspace__action"
                :disabled="activeGrammarFieldState.status === 'loading' || activeGrammarFieldState.status === 'applying' || !activeGrammarFieldText.trim()"
                @click="refreshActiveGrammarWorkspace"
              >
                {{ activeGrammarFieldState.status === 'loading' ? 'Checking...' : 'Scan Again' }}
              </button>
              <button type="button" class="grammar-workspace__close" @click="closeGrammarWorkspace">
                Close
              </button>
            </div>
          </div>

          <div class="grammar-workspace__body">
            <div class="grammar-workspace__status">
              <strong>{{ grammarPanelTitle(activeGrammarFieldKey, activeGrammarFieldText) }}</strong>
              <span>{{ grammarPanelCopy(activeGrammarFieldKey, activeGrammarFieldText) }}</span>
            </div>

            <div v-if="!activeGrammarFieldText.trim()" class="grammar-workspace__empty">
              Add some English text in this section and open Grammar Assistant again to review the wording.
            </div>

            <div v-else-if="activeGrammarFieldState.error" class="grammar-message grammar-message--error">
              {{ activeGrammarFieldState.error }}
            </div>

            <div
              v-else-if="activeGrammarFieldState.status === 'loading' || activeGrammarFieldState.status === 'applying'"
              class="grammar-workspace__loading"
            >
              <div class="grammar-workspace__pulse"></div>
              <p>{{ activeGrammarFieldState.status === 'applying' ? 'Applying your selected suggestion and refreshing the latest advice.' : 'Reading this section and preparing suggestions.' }}</p>
            </div>

            <div v-else-if="isGrammarFieldStale(activeGrammarFieldKey, activeGrammarFieldText)" class="grammar-message grammar-message--stale">
              The text changed after the previous scan. Use `Scan Again` to refresh the suggestions for this latest draft.
            </div>

            <div v-else-if="activeGrammarFieldState.checkedAt && !activeGrammarFieldState.results.length" class="grammar-workspace__success">
              <div class="grammar-workspace__success-mark">OK</div>
              <div>
                <strong>No issues found</strong>
                <p>This section looks clean on the latest grammar pass.</p>
              </div>
            </div>

            <div v-else-if="activeGrammarFieldState.results.length" class="grammar-issues">
              <div
                v-for="issue in activeGrammarFieldState.results"
                :key="issue.id"
                class="grammar-issue"
              >
                <div class="grammar-issue__meta">
                  <span class="grammar-issue__kind">{{ issue.category }}</span>
                  <span v-if="issue.problemText" class="grammar-issue__problem">"{{ issue.problemText }}"</span>
                </div>
                <p class="grammar-issue__message">{{ issue.message }}</p>
                <div v-if="issue.suggestions.length" class="grammar-suggestions">
                  <button
                    v-for="suggestion in issue.suggestions"
                    :key="suggestion.id"
                    type="button"
                    class="grammar-suggestion-btn"
                    :disabled="activeGrammarFieldState.status === 'applying'"
                    @click="applyActiveGrammarSuggestion(issue, suggestion)"
                  >
                    {{ suggestion.actionLabel }}
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="grammar-workspace__empty">
              Open an additional note or article chip to scan that part of the agreement.
            </div>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import Service from '@/service/api'
import { COUNTRY_OPTIONS } from '@/utils/countries'
import {
  buildAgreementArticleChipLabel,
  getAgreementSectionDisplayTitle,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'
import { applyHarperGrammarSuggestion, lintHarperGrammar, warmupHarperGrammar } from '@/utils/harper-grammar'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import AgreementSignaturePreview from '@/views/shared/AgreementSignaturePreview'

const TEMPLATE_SECTION_MAP = {
  'Exchange Student': [
    { key: 'overview', label: 'Agreement Purpose', description: 'Student exchange purpose and reference to the MOU between the parties.' },
    { key: 'partner-profile', label: 'Parties', description: 'Mae Fah Luang University and partner institution identification.' },
    { key: 'mobility', label: 'Exchange Conditions', description: 'Exchange student numbers, period of exchange, and host university conditions.' },
    { key: 'provisions', label: 'Student Responsibilities', description: 'Fees, living costs, insurance, and student obligations.' },
    { key: 'credits', label: 'Academic Record & Credit Transfer', description: 'Transcript handling and home-university credit recognition.' },
    { key: 'eligibility', label: 'Academic Status & Proficiency', description: 'Student status, English proficiency, and graduation status rules.' },
    { key: 'timeline', label: 'Effective Period', description: 'Effective date, five-year term, and written extension terms.' },
    { key: 'legal', label: 'Termination & Language', description: 'Termination notice and governing language version.' },
    { key: 'signatures', label: 'Signature Block', description: 'MFU and partner signatory areas and execution details.' }
  ],
  'Double Degree': [
    { key: 'overview', label: 'Programme Summary', description: 'Double degree programme objective and overall arrangement between the parties.' },
    { key: 'partner-profile', label: 'Definitions & Parties', description: 'Party definitions, home/host university roles, and DDP framing.' },
    { key: 'curriculum', label: 'Programme Structure', description: 'Study plan, credit transfer, and academic pathway across both institutions.' },
    { key: 'registration', label: 'Registration', description: 'Student screening, application timeline, and admission process.' },
    { key: 'finance', label: 'Financial Arrangements', description: 'Tuition, insurance, travel, accommodation, and funding responsibilities.' },
    { key: 'timeline', label: 'Duration of Study', description: 'Minimum study period, extension possibilities, and maximum programme duration.' },
    { key: 'certification', label: 'Awarding the Degree', description: 'Degree requirements and conferral rules of both institutions.' },
    { key: 'research', label: 'Course Work & Research Projects', description: 'Advisors, joint study plan, and research supervision responsibilities.' },
    { key: 'examination', label: 'Credits, Committee & Final Examination', description: 'Dissertation, oral defence, committee participation, and evaluation.' },
    { key: 'quality', label: 'Graduation, Management & Student Services', description: 'Graduation documents, management committees, and student services.' },
    { key: 'legal', label: 'IP, Amendments & Validity', description: 'Intellectual property, dispute settlement, amendment rules, and validity period.' },
    { key: 'signatures', label: 'Authenticity & Signatures', description: 'English execution copies and dean/institutional signature block.' }
  ],
  'Extension Agreement': [
    { key: 'overview', label: 'Extension Title', description: 'Extension of MOU heading and party identification.' },
    { key: 'original-agreement', label: 'Original Memorandum', description: 'Reference to the original MOU and signed date.' },
    { key: 'continuation', label: 'Original Terms Continue', description: 'Confirmation that the original terms and conditions remain in effect.' },
    { key: 'extension-setup', label: 'Extended Period', description: 'New five-year period and updated effective dates.' },
    { key: 'timeline', label: 'Effective Dates', description: 'From-date and to-date of the extension period.' },
    { key: 'signatures', label: 'Signature Block', description: 'Signed on behalf of MFU and the partner institution.' }
  ],
  'MOU File': [
    { key: 'overview', label: 'MOU Purpose', description: 'Academic cooperation and exchange purpose between the parties.' },
    { key: 'partner-profile', label: 'Parties', description: 'MFU and partner institution identification in the MOU opening.' },
    { key: 'activities', label: 'Academic Activities', description: 'Exchange, dual degree, research, lectures, symposia, and related activities.' },
    { key: 'finance', label: 'Financial Arrangements', description: 'Financial matters handled under separate agreements.' },
    { key: 'governance', label: 'Amendments', description: 'Mutual consent for modification or amendment of the MOU.' },
    { key: 'legal', label: 'Termination', description: 'Six-month termination notice and protection for approved activities.' },
    { key: 'duration', label: 'Effective Period & Language', description: 'Five-year validity, extension, and governing language copy.' },
    { key: 'signatures', label: 'Signature Block', description: 'MFU and partner signature section and dates.' }
  ]
}

const EXACT_ARTICLE_TEMPLATES = {
  'Exchange Student': (form) => [
    {
      key: 'title-block',
      title: 'Agreement on Student Exchange',
      content: `Agreement on Student Exchange\nbetween\nMae Fah Luang University, Kingdom of Thailand\nand\n${form.partner || '..............................'}, ${form.country || '..............................'}`
    },
    {
      key: 'purpose',
      title: 'Purpose',
      content: `Mae Fah Luang University, Kingdom of Thailand and ${form.partner || '..............................'} (hereinafter referred to individually as the “Party” and collectively as the “Parties”), hereby agree to this Agreement on Student Exchange (hereinafter referred to as the “Agreement”) for the purpose of promoting student exchange outlined in the Memorandum of Understanding (hereinafter referred to as “MOU”) between the Parties.`
    },
    {
      key: 'exchange-conditions',
      title: 'Exchange Conditions',
      content: `The Parties agree on the exchange of students in accordance with the following conditions:\n\n- The students participating in the exchange program (hereinafter referred to as “exchange students”) shall obtain admission to the host university based on its selection criteria.\n- The number of exchange students per year shall be agreed upon based on mutual demands and the balance of the Parties.\n- The period of exchange for each exchange student shall be within one year.\n- The status of the exchange students shall be “exchange students” at Mae Fah Luang University and “${form.templateFields.scope || '..............................'}” at ${form.partner || '..............................'}.\n- The host university shall not charge the exchange students for application fees, admission fees, and tuition fees.\n- All expenses including board and lodging, costs of living, accommodation expenses, travel expenses, medical costs, and insurance fees shall be the responsibility of the exchange students.`
    },
    {
      key: 'academic-record',
      title: 'Academic Record and Credit Transfer',
      content: `- The host university shall evaluate the academic performance of each exchange student according to its rules and shall send the home university the academic record/transcript of each exchange student.\n- The home university shall recognize the credits earned by the exchange students at the host university under the credit transfer system of the home university.`
    },
    {
      key: 'student-status',
      title: 'Student Status and Proficiency',
      content: `- The exchange students shall be subject to the rules and regulations for students at the host university.\n- The exchange students shall possess sufficient English language proficiency in order to attend lectures and conduct research at the host university.\n- The exchange students shall remain a candidate for a graduation degree at the home university and shall not be a candidate for a degree at the host university.`
    },
    {
      key: 'effective-period',
      title: 'Effective Period',
      content: `This Agreement shall enter into effect from the date duly signed by the Parties. The effective period is five (5) years. The agreed-upon period may be further extended by subsequent agreements in writing made before it expires.`
    },
    {
      key: 'termination',
      title: 'Termination',
      content: `This Agreement may be terminated upon six (6) months prior written notice without cause by either Party. In such a case, the programs or activities already approved by the Parties shall be allowed to be completed under the conditions of this Agreement.`
    },
    {
      key: 'language',
      title: 'Language',
      content: `This Agreement is signed in two copies in English and ${form.templateFields.language || '..............'} language. Each Party to the MOU shall retain one copy. In the event of any discrepancy between the aforementioned versions, the English version shall prevail.`
    },
    {
      key: 'signatures',
      title: 'Signature Block',
      content: `Mae Fah Luang University || ${form.partner || '..............................'}\nAsst. Prof. Matchima Naradisorn, Ph.D. || (Name) ${form.contactPerson || '..............................'}\nPresident || (Position) ${form.templateFields.terms || '..............................'}\nDate: || Date:`
    }
  ],
  'Extension Agreement': (form) => [
    {
      key: 'title-block',
      title: 'Extension of Memorandum of Understanding',
      content: `Extension of Memorandum of Understanding\n\nbetween\n\nMae Fah Luang University, Thailand\nand\n${form.partner || '....................................'}, ${form.country || '....................'}`
    },
    {
      key: 'extension-confirmation',
      title: 'Extension Confirmation',
      content: `Mae Fah Luang University and ${form.partner || '......................'} confirm that the following Memorandum of Understanding has been extended under the original terms and conditions for an additional period:`
    },
    {
      key: 'original-mou',
      title: 'Original Memorandum of Understanding',
      content: `Memorandum of Understanding (signed on ${form.templateFields.scope || '......................'})`
    },
    {
      key: 'extension-period',
      title: 'Extension Period',
      content: `The expiration date of the above Memorandum of Understanding is hereby extended for a period of five (5) years from ${form.templateFields.duration || '......................'} to ${form.templateFields.terms || '......................'}.`
    },
    {
      key: 'signature-block',
      title: 'Signature Block',
      content: `Signed on behalf of\nMae Fah Luang University\n\nSigned on behalf of\n${form.partner || '..............................'}`
    },
    {
      key: 'signatories',
      title: 'Signatories',
      content: `Asst. Prof. Matchima Naradisorn, PhD\n${form.contactPerson || '....................................'}\nPresident\nPresident\n\nDate:\nDate:`
    }
  ],
  'MOU File': (form) => [
    {
      key: 'title-block',
      title: 'Memorandum of Understanding',
      content: `Memorandum of Understanding\nbetween\nMae Fah Luang University, Kingdom of Thailand\nand\n${form.partner || '..............................'}, ${form.country || '..............................'}`
    },
    {
      key: 'purpose',
      title: 'Purpose',
      content: `Mae Fah Luang University, Kingdom of Thailand and ${form.partner || '..............................'}, (hereinafter referred to individually as the “Party” and collectively as the “Parties”), hereby agree to encourage and promote academic cooperation and exchange between the Parties. The scope and procedural details of the Memorandum of Understanding (hereinafter referred to as “MOU”) are as follows:`
    },
    {
      key: 'activities',
      title: 'Academic Activities',
      content: `The Parties agree to encourage and promote the following academic activities:\n(a) Exchange of faculty and staff members,\n(b) Exchange of students,\n(c) Dual degree programs,\n(d) Joint research, lectures and symposia,\n(e) Other activities deemed appropriate by mutual consent.`
    },
    {
      key: 'financial-arrangements',
      title: 'Financial Arrangements',
      content: `Financial arrangements and stipulations concerning the activities under this MOU shall be described and agreed upon by the Parties in a separate Agreement.`
    },
    {
      key: 'amendments',
      title: 'Modifications and Amendments',
      content: `Modifications and/or amendments to this MOU shall be instigated by mutual consent.`
    },
    {
      key: 'termination',
      title: 'Termination',
      content: `This MOU may be terminated upon six (6) months prior written notice without cause by either Party in advance of the desired termination date. In such a case, the programs or activities already approved by the Parties shall be allowed to be completed under the conditions of this MOU.`
    },
    {
      key: 'effective-period',
      title: 'Effective Period and Language',
      content: `This MOU shall be effective upon the date of signature by representatives of the Parties. The effective period is five (5) years. The agreed-upon period may be further extended by subsequent agreements in writing made before it expires.\n\nThis MOU is signed in two (2) copies in English and ${form.templateFields.language || '..............'} language. Each Party to the MOU shall retain one copy. In the event of any discrepancy between the aforementioned versions, the English version shall prevail.`
    },
    {
      key: 'signatures',
      title: 'Signature Block',
      content: `Mae Fah Luang University || ${form.partner || '..............................'}\nAsst. Prof. Matchima Naradisorn, Ph.D. || (Name) ${form.contactPerson || '..............................'}\nPresident || (Position) ${form.templateFields.terms || '..............................'}\nDate: || Date:`
    }
  ],
  'Double Degree': (form) => [
    {
      key: 'title-block',
      title: 'Agreement',
      content: `AGREEMENT FOR A DOUBLE DEGREE PROGRAM\nBETWEEN\n${form.department || 'International Affairs'},\nMAE FAH LUANG UNIVERSITY, THAILAND\nAND\n${form.partner || '................................................'},\n${form.country || '................................................'}`
    },
    {
      key: 'opening',
      title: 'Opening',
      content: `This Agreement for a Double Degree Programme (the “Agreement”) between Mae Fah Luang University and ${form.partner || '..............................'}, ${form.department || 'School of ..............................'}, Mae Fah Luang University, Thailand and ${form.partner || '..............................'} agree to the following:`
    },
    {
      key: 'article-1',
      title: 'Article 1: Definitions',
      content: `- ${form.department || '..............................'} and Mae Fah Luang University, each represented by its Dean, shall hereinafter be referred to collectively as the “Parties” or individually as a “Party”.\n- “Graduate Students” refers to the students participating in the double degree programme, specifically a joint doctorate programme offered by the Parties.\n- “DDP” refers to the double degree programme for Graduate Students pursuing a doctorate from the Parties, based on their existing programmes.\n- “Home University” refers to the university that sends students to the Host University.\n- “Host University” refers to the university that receives students from the Home University.`
    },
    {
      key: 'article-2',
      title: 'Article 2: Programme Summary',
      content: `- The Parties have established a partnership to offer a DDP for Graduate Students to earn a doctorate, based on their existing programmes, whereby Graduate Students shall study at both universities. Upon completion of the DDP, Graduate Students shall receive two degrees recognising and cross-referencing completion of the requirements for a doctorate at the Parties.\n- The DDP requires that Graduate Students spend at least one academic year at Mae Fah Luang University and at least one academic year at the partner university. The Parties agree to transfer semester credits obtained by a Graduate Student in the other Party so that they shall be taken into account for the degree awarded by the home university.\n- The Parties agree to use the standard system of their country for credit transfer.`
    },
    {
      key: 'article-3',
      title: 'Article 3: Registration',
      content: `- The host university may accept one Graduate Student; however, if there is more than one applicant, the Parties shall mutually decide whether additional students can be accepted at least three months in advance.\n- The home university shall initially screen the student applicants according to its terms and regulations.\n- On or before the calendar date, the home university shall immediately forward the host university a list of selected Graduate Students with the necessary documents for verification, review and final approval.\n- Interviews may be conducted to gauge the academic performance, research skills and language proficiency of the applicants.\n- Graduate Students from the Parties are required to demonstrate an appropriately high level of English language proficiency at the time of admission.`
    },
    {
      key: 'article-4',
      title: 'Article 4: Financial Arrangements',
      content: `- Graduate Students shall pay tuition, insurance and other fees to the home university throughout the duration of the DDP.\n- They shall be exempt from tuition, admission and registration fees at the host university.\n- Graduate Students are expected to provide their own funding and must prove that they have sufficient funds for transportation, accommodation, food, health insurance and other expenses.`
    },
    {
      key: 'article-5',
      title: 'Article 5: Duration of Study',
      content: `The DDP requires that each Graduate Student spend at least one academic year at the home and host university. However, the study period may be extended at the home and host university upon mutual agreement by the Parties. The maximum DDP study period shall be six years. Graduate Students shall be accepted for enrolment at the beginning of the first or second semester at Mae Fah Luang University as well as the partner university.`
    },
    {
      key: 'article-6',
      title: 'Article 6: Requirements for Awarding the Degree',
      content: `Graduate Students are expected to fulfil all their required course work, written dissertation and any other requirements during their doctoral degree programme at the Parties. The formal requirements for conferral of a degree from Mae Fah Luang University are stated in the Regulations of the Graduate School of Mae Fah Luang University, whereas the requirements of the partner university are found in its own graduate regulations.`
    },
    {
      key: 'article-7',
      title: 'Article 7: Course Work and Research Projects',
      content: `- The host university shall assign an appropriate academic advisor for each Graduate Student prior to arrival.\n- Academic advisors from the Parties are expected to work collaboratively to provide academic guidance and assistance.\n- The academic advisors of the Parties shall identify in advance the graduate research project that students need to undertake.\n- The co-advisor at the host university shall be responsible for periodically updating the main advisor at the home university on academic and research progress.\n- The host university shall provide the Graduate Students with the necessary services to support communication and research activity.`
    },
    {
      key: 'article-8',
      title: 'Article 8: Credits, Committee and Academic Advisers, Dissertation and Final Examination',
      content: `- The home university shall recognise the credits earned by Graduate Students at the host university under the credit transfer system of the home university.\n- The Parties agree to organise the DDP committees of the Graduate Students in each institution.\n- Each Graduate Student is required to submit two dissertations on related subjects but different topics.\n- The final oral examination shall be held at either the home or the host university via video conferencing or other means of telecommunication technology.\n- Both the main advisor and co-advisor must attend the presentation.\n- The degree shall be conferred based on the existing regulations implemented and governed by each Party.\n- The home institution shall pay the remuneration of the joint dissertation committees and the examiner.`
    },
    {
      key: 'article-9',
      title: 'Article 9: Graduation',
      content: `The Parties shall issue each a certificate for the academic degree according to its regulations. Additionally, the certificates shall be accompanied by a bridge document containing relevant facts about the Programme.`
    },
    {
      key: 'article-10',
      title: 'Article 10: Intellectual Property',
      content: `The research results generated solely by Mae Fah Luang University shall reside in Mae Fah Luang University; research results generated solely by the partner university shall reside in that university. Jointly generated research results shall not be published, disclosed, or commercially exploited without prior written consent of both Parties.`
    },
    {
      key: 'article-11',
      title: 'Article 11: Management of the Programme',
      content: `The Parties agree to nominate the working committees and secretariat in each university to carry out the work of this DDP at each university.`
    },
    {
      key: 'article-12',
      title: 'Article 12: Administrative Services for Students',
      content: `Graduate Students who are successfully admitted in this DDP must register and complete matriculation procedures of the Parties on the date set by the Parties. They shall be recognised and shall receive benefits equal to regular full-time students at the host university, while complying with the host university regulations.`
    },
    {
      key: 'article-13',
      title: 'Article 13: Amendment, Modification and Dispute Settlement',
      content: `This Agreement shall be amended or corrected only through the written consent of the Parties. Any dispute that may arise in the implementation or interpretation of this Agreement shall be resolved amicably through mutual consultations between the Parties.`
    },
    {
      key: 'article-14',
      title: 'Article 14: Effective Date and Period of Validity of the Agreement',
      content: `This Agreement shall become effective from the date of signature by the representatives of the Parties for a period of five (5) years and shall be automatically renewed for another five (5) years unless either Party undertakes in writing to withdraw from this Agreement. A written declaration of withdrawal must be made no later than six (6) months prior to the intended termination date.\n\nAny matter not stipulated herein shall require accord between the Parties and shall be documented in a supplementary agreement. Other written materials related to the DDP shall be shared, recognised and preserved between the Parties.`
    },
    {
      key: 'article-15',
      title: 'Article 15: Authenticity',
      content: `This Agreement is signed in two copies in English. Each Party to the Agreement shall retain one copy.`
    },
    {
      key: 'signatures',
      title: 'Signature Block',
      content: `Mae Fah Luang University || ${form.partner || '................................'}\nAsst. Prof. Dr. Sutthiwal Setha || Prof. Dr. ${form.contactPerson || '................................'}\nDean of ${form.department || 'International Affairs'} || Dean of ${form.partner || '................................'}\nDate: || Date:`
    }
  ]
}

const TEMPLATE_COPY_MAP = {
  overview(form) {
    if (form.activityType === 'Exchange Student') {
      return `Mae Fah Luang University, Kingdom of Thailand and ${form.partner || 'the partner institution'} hereby agree to this Agreement on Student Exchange for the purpose of promoting student exchange outlined in the Memorandum of Understanding between the parties.`
    }
    if (form.activityType === 'Double Degree') {
      return `This Agreement for a Double Degree Programme sets out the intention of Mae Fah Luang University and ${form.partner || 'the partner institution'} to establish a joint doctoral-level academic arrangement under a shared programme structure.`
    }
    if (form.activityType === 'Extension Agreement') {
      return `This Extension of Memorandum of Understanding confirms that Mae Fah Luang University and ${form.partner || 'the partner institution'} agree to continue the existing collaboration under the extension terms stated in this document.`
    }
    return `Mae Fah Luang University, Kingdom of Thailand and ${form.partner || 'the partner institution'} agree to encourage and promote academic cooperation and exchange through this Memorandum of Understanding.`
  },
  'partner-profile'(form) {
    if (form.activityType === 'Double Degree') {
      return `${form.department || 'The relevant school'} of Mae Fah Luang University, Thailand and ${form.partner || 'the partner institution'} are the parties to this programme and act as the home and host universities for participating graduate students.`
    }
    return `Mae Fah Luang University, Kingdom of Thailand and ${form.partner || 'the partner institution'} in ${form.country || 'the partner country'} are the named parties to this agreement and will act through their authorized institutional representatives.`
  },
  eligibility(form) {
    return `Participating students must satisfy the host university's selection criteria, remain in good academic standing, comply with host-university rules and regulations, demonstrate sufficient English language proficiency, and remain candidates for graduation at the home university only.`
  },
  credits(form) {
    return form.activityType === 'Exchange Student'
      ? `The host university shall evaluate the academic performance of each exchange student and send the academic record or transcript to the home university. The home university shall recognize the credits earned under its own credit transfer system.`
      : `The home university shall recognise credits earned by graduate students at the host university under its own credit transfer system, and both institutions shall coordinate the necessary academic record treatment for programme completion.`
  },
  mobility(form) {
    return `The parties agree on the exchange of students in accordance with mutual demand and balance. The number of exchange students per year will be mutually agreed, and the period of exchange for each student shall remain within one year.`
  },
  provisions(form) {
    return `The host university shall not charge application, admission, or tuition fees to exchange students. Board, lodging, living costs, accommodation, travel, medical costs, and insurance remain the responsibility of the exchange students.`
  },
  curriculum(form) {
    return `The parties agree that graduate students shall study at both universities under an agreed study plan, and that semester credits obtained at one institution shall be taken into account for the degree awarded by the home university.`
  },
  registration(form) {
    return `The host university may accept one graduate student unless the parties mutually decide otherwise. Screening, interview, academic review, language assessment, and final approval should be completed in advance in accordance with the agreed timeline.`
  },
  finance(form) {
    return form.activityType === 'MOU File'
      ? `Financial arrangements and stipulations concerning activities under this MOU shall be described and agreed upon by the parties in a separate agreement.`
      : `Graduate students shall pay tuition, insurance, and other fees to the home university throughout the programme and remain responsible for transportation, accommodation, food, insurance, and other living expenses.`
  },
  research(form) {
    return `Academic advisors from the parties shall work collaboratively to guide the graduate students, identify research topics in advance, support the detailed study and research plan, and coordinate regular progress updates between the home and host institutions.`
  },
  quality(form) {
    return form.activityType === 'Double Degree'
      ? `The parties shall issue degree certificates and supporting academic documentation according to their regulations, nominate working committees and secretariat personnel, and make efforts to provide student services, accommodation support, and immigration guidance.`
      : `The parties shall monitor implementation quality and keep the agreement aligned with the institutional standards and responsibilities accepted by both sides.`
  },
  examination(form) {
    return `The parties shall organise dissertation committees, appoint a main advisor and co-advisor, conduct the final oral examination in English where appropriate, and define the treatment of dissertation evaluation, remuneration, and related intellectual property matters.`
  },
  certification(form) {
    return `Graduate students are expected to fulfil required course work, dissertation obligations, and any other formal degree requirements of both institutions before the degree is conferred according to each party's regulations.`
  },
  continuation(form) {
    return `Mae Fah Luang University and ${form.partner || 'the partner institution'} confirm that the original Memorandum of Understanding is extended under the original terms and conditions for an additional period.`
  },
  'original-agreement'(form) {
    return `The extension refers to the original Memorandum of Understanding signed on the prior execution date and uses that instrument as the basis for the continued relationship between the parties.`
  },
  timeline(form) {
    if (form.activityType === 'Exchange Student') {
      return `This Agreement shall enter into effect from the date duly signed by the parties. The effective period is five years, and the period may be further extended by subsequent written agreement before expiry.`
    }
    if (form.activityType === 'Double Degree') {
      return `The double degree programme requires each graduate student to spend at least one academic year at each institution, and the Agreement itself shall remain effective for five years unless written withdrawal is provided no later than six months before termination.`
    }
    return `The expiration date of the original Memorandum of Understanding is hereby extended for a period of five years from the agreed effective date to the new expiration date recorded by the parties.`
  },
  'extension-setup'(form) {
    return `The extension confirms an additional five-year period and records the new timeline for the continuing validity of the Memorandum of Understanding between the parties.`
  },
  governance(form) {
    return form.activityType === 'MOU File'
      ? `Modifications or amendments to this Memorandum of Understanding shall be made only through mutual consent of the parties.`
      : `Each institution should designate responsible representatives and maintain mutual communication for execution of the agreement and any administrative coordination required.`
  },
  activities(form) {
    return `The parties agree to encourage and promote academic activities including exchange of faculty and staff members, exchange of students, dual degree programmes, joint research, lectures and symposia, and other activities deemed appropriate by mutual consent.`
  },
  duration(form) {
    return `This MOU shall become effective upon signature, remain valid for five years, and may be further extended by written agreement before expiry. It is signed in two copies in English and the partner language, with the English version prevailing in case of discrepancy.`
  },
  legal(form) {
    if (form.activityType === 'Exchange Student') {
      return `This Agreement may be terminated by either party upon six months' prior written notice without cause. It is signed in English and the partner language, and the English version shall prevail in the event of discrepancy.`
    }
    if (form.activityType === 'Double Degree') {
      return `Research results generated solely by one party shall reside in that party, while jointly generated results shall not be published, disclosed, or commercially exploited without prior written consent of both parties. Any dispute shall be resolved amicably through mutual consultation.`
    }
    return `This MOU may be terminated by either party upon six months' prior written notice without cause, and approved programmes or activities already underway shall be allowed to be completed under the existing conditions.`
  },
  signatures(form) {
    return `The final document should retain the signature areas for Mae Fah Luang University and ${form.partner || 'the partner institution'}, including names, official positions, and execution dates for both parties.`
  }
}

function normalizeClauseId(item) {
  return item && (item.id || item._id) ? String(item.id || item._id) : ''
}

const CLAUSE_ARTICLE_KEY_PREFIX = 'selected-clause-article-'

function buildClauseArticleKey(clauseId) {
  const id = String(clauseId || '').trim()
  return id ? `${CLAUSE_ARTICLE_KEY_PREFIX}${id}` : ''
}

function parseClauseArticleId(key) {
  const value = String(key || '').trim()
  return value.startsWith(CLAUSE_ARTICLE_KEY_PREFIX) ? value.slice(CLAUSE_ARTICLE_KEY_PREFIX.length) : ''
}

function createEmptyGrammarState() {
  return {
    status: 'idle',
    error: '',
    results: [],
    checkedAt: '',
    checkedText: ''
  }
}

function formatGrammarError(error) {
  const message = error && error.message ? String(error.message) : ''
  return message || 'Grammar Assistant could not check this text right now.'
}

function buildSuggestionActionLabel(suggestion, index) {
  const replacement = String(suggestion && suggestion.get_replacement_text ? suggestion.get_replacement_text() : '').trim()
  const kind = suggestion && suggestion.kind ? suggestion.kind() : 0
  const compactReplacement = replacement.length > 40 ? `${replacement.slice(0, 37).trim()}...` : replacement

  if (kind === 1) return 'Remove text'
  if (kind === 2) return compactReplacement ? `Insert "${compactReplacement}"` : 'Insert suggestion'
  return compactReplacement ? `Use "${compactReplacement}"` : `Suggestion ${index + 1}`
}

export default {
  name: 'UserAgreementEditorBase',
  components: {
    AgreementDocumentHeader,
    AgreementSignaturePreview
  },
  props: {
    agreementType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      error: '',
      success: '',
      isBootstrappingDraft: false,
      articlePage: 1,
      articlesPerPage: 4,
      form: {
        title: '',
        partner: '',
        country: [],
        partnerEmail: '',
        contactPerson: '',
        department: 'International Affairs',
        summary: '',
        activityType: '',
        draftSource: 'builder',
        templateSelections: [],
        templateFields: {
          scope: '',
          duration: '',
          language: 'English',
          terms: ''
        },
        articleDrafts: [],
        customDocument: {
          title: '',
          content: ''
        },
        uploadedDraft: null,
        partnerLogo: null
      },
      builderDraftCache: [],
      importedDraftCache: [],
      availableClauses: [],
      selectedClauseIds: [],
      suggestedClauseIds: [],
      activeClauseCategory: 'all',
      clauseSearchQuery: '',
      countrySearch: '',
      countryPickerOpen: false,
      modeOptions: [
        { value: 'builder', label: 'Draft Builder' },
        { value: 'upload', label: 'Import Contract' }
      ],
      activeMoveArticleIndex: null,
      draggedIndex: null,
      dropTargetIndex: null,
      expandedClauseIds: [],
      grammarFields: {},
      grammarWorkspaceVisible: false,
      activeGrammarFieldKey: ''
    }
  },
  computed: {
    agreementId() {
      return this.$route.params.id && this.$route.params.id !== 'new' ? this.$route.params.id : ''
    },
    hasSavedDraft() {
      return !!this.agreementId
    },
    isViewMode() {
      return this.$route.query && this.$route.query.mode === 'view'
    },
    templateSections() {
      return TEMPLATE_SECTION_MAP[this.agreementType] || TEMPLATE_SECTION_MAP['MOU File']
    },
    draftModeLabel() {
      const found = this.modeOptions.find(item => item.value === this.form.draftSource)
      return found ? found.label : 'Draft'
    },
    acceptedContractTypes() {
      return '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    hasImportedEditableSections() {
      return this.form.draftSource === 'upload' && Array.isArray(this.form.articleDrafts) && this.form.articleDrafts.some((item) => {
        return String(item && (item.title || item.content || '')).trim()
      })
    },
    totalArticlePages() {
      return Math.max(1, Math.ceil((this.form.articleDrafts || []).length / this.articlesPerPage))
    },
    pageStartIndex() {
      return (this.articlePage - 1) * this.articlesPerPage
    },
    pagedArticles() {
      return (this.form.articleDrafts || []).slice(this.pageStartIndex, this.pageStartIndex + this.articlesPerPage)
    },
    previewTitle() {
      return this.form.title || `${this.agreementType} Draft`
    },
    previewSections() {
      return this.buildDocumentPreviewSections()
    },
    countryOptions() {
      const values = Array.isArray(COUNTRY_OPTIONS) ? [...COUNTRY_OPTIONS] : []
      const currentValues = this.normalizeCountrySelection(this.form.country)
      currentValues.forEach((current) => {
        if (current && !values.includes(current)) values.unshift(current)
      })
      return values
    },
    selectedCountryValues() {
      return this.normalizeCountrySelection(this.form.country)
    },
    filteredCountryOptions() {
      const selected = new Set(this.selectedCountryValues)
      const query = this.normalizeCountrySearch(this.countrySearch)
      const options = this.countryOptions.filter(country => !selected.has(country))
      const filtered = query
        ? options.filter(country => this.countryMatchesSearch(country, query))
        : options
      return filtered.slice(0, 12)
    },
    selectedCountryText() {
      return this.formatCountryList(this.form.country)
    },
    countryInputPlaceholder() {
      return 'Select one or more countries'
    },
    clauseCategoryOptions() {
      const categories = [...new Set(this.availableClauses.map((item) => String(item.category || 'General')).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right))
      return [{ value: 'all', label: 'All Categories' }].concat(categories.map((item) => ({
        value: item,
        label: item
      })))
    },
    filteredClauseOptions() {
      const selectedSet = new Set(this.selectedClauseIds.map((item) => String(item)))
      const suggestedSet = new Set(this.suggestedClauseIds.map((item) => String(item)))
      let items = Array.isArray(this.availableClauses) ? this.availableClauses.slice() : []

      if (this.activeClauseCategory !== 'all') {
        const category = String(this.activeClauseCategory || '').trim().toLowerCase()
        items = items.filter((item) => String(item.category || 'General').trim().toLowerCase() === category)
      }

      if (this.clauseSearchQuery) {
        const query = String(this.clauseSearchQuery || '').trim().toLowerCase()
        items = items.filter((item) => {
          return String(item.title || '').toLowerCase().includes(query)
            || String(item.category || '').toLowerCase().includes(query)
            || String(item.content || '').toLowerCase().includes(query)
        })
      }

      return items.sort((left, right) => {
        const leftSelected = selectedSet.has(left.id)
        const rightSelected = selectedSet.has(right.id)
        if (leftSelected !== rightSelected) return leftSelected ? -1 : 1

        const leftSuggested = suggestedSet.has(left.id)
        const rightSuggested = suggestedSet.has(right.id)
        if (leftSuggested !== rightSuggested) return leftSuggested ? -1 : 1

        const categoryCompare = String(left.category || 'General').localeCompare(String(right.category || 'General'))
        if (categoryCompare !== 0) return categoryCompare
        return String(left.title || '').localeCompare(String(right.title || ''))
      })
    },
    selectedClauseCount() {
      return Array.isArray(this.selectedClauseIds) ? this.selectedClauseIds.length : 0
    },
    uploadedDraftUrl() {
      return this.form.uploadedDraft && this.form.uploadedDraft.url ? this.form.uploadedDraft.url : ''
    },
    partnerLogoName() {
      return this.form.partnerLogo && this.form.partnerLogo.name ? this.form.partnerLogo.name : 'No partner logo uploaded'
    },
    activeGrammarFieldState() {
      return this.activeGrammarFieldKey ? this.grammarFieldState(this.activeGrammarFieldKey) : createEmptyGrammarState()
    },
    activeGrammarFieldText() {
      return this.grammarFieldCurrentText(this.activeGrammarFieldKey)
    },
    activeGrammarFieldLabel() {
      return this.grammarFieldLabel(this.activeGrammarFieldKey)
    }
  },
  watch: {
    agreementId(nextId, previousId) {
      if (nextId && nextId !== previousId) {
        this.loadAgreement()
      }
    }
  },
  mounted() {
    this.loadAgreement()
    this.queueGrammarWarmup()
  },
  methods: {
    openCountryPicker() {
      if (this.isViewMode) return
      this.countryPickerOpen = true
    },
    handleCountryPickerBlur() {
      window.setTimeout(() => {
        const picker = this.$refs.countryPicker
        if (picker && picker.contains(document.activeElement)) return
        this.countryPickerOpen = false
      }, 0)
    },
    normalizeCountrySearch(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    },
    countryMatchesSearch(country, query) {
      const normalizedCountry = this.normalizeCountrySearch(country)
      const countryTokens = normalizedCountry.split(/\s+/).filter(Boolean)
      const queryTokens = this.normalizeCountrySearch(query).split(/\s+/).filter(Boolean)
      if (!queryTokens.length) return true
      return queryTokens.every(token => (
        normalizedCountry.includes(token) ||
        countryTokens.some(countryToken => countryToken.startsWith(token))
      ))
    },
    addCountry(country) {
      const nextCountry = String(country || '').trim()
      if (!nextCountry) return
      const values = this.selectedCountryValues
      if (!values.includes(nextCountry)) {
        this.form.country = values.concat(nextCountry)
      }
      this.countrySearch = ''
      this.countryPickerOpen = true
    },
    removeCountry(country) {
      const nextCountry = String(country || '').trim()
      this.form.country = this.selectedCountryValues.filter(item => item !== nextCountry)
    },
    selectFirstFilteredCountry() {
      if (!this.filteredCountryOptions.length) return
      this.addCountry(this.filteredCountryOptions[0])
    },
    removeLastCountryIfSearchEmpty(event) {
      if (String(this.countrySearch || '').length) return
      const values = this.selectedCountryValues
      if (!values.length) return
      event.preventDefault()
      this.form.country = values.slice(0, -1)
    },
    queueGrammarWarmup() {
      if (this.isViewMode) return

      const warmup = () => {
        warmupHarperGrammar().catch(() => {})
      }

      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => warmup(), { timeout: 1200 })
        return
      }

      if (typeof window !== 'undefined') {
        window.setTimeout(() => warmup(), 400)
        return
      }

      warmup()
    },
    grammarFieldState(fieldKey) {
      return this.grammarFields[fieldKey] || createEmptyGrammarState()
    },
    ensureGrammarFieldState(fieldKey) {
      if (!this.grammarFields[fieldKey]) {
        this.$set(this.grammarFields, fieldKey, createEmptyGrammarState())
      }
      return this.grammarFields[fieldKey]
    },
    articleGrammarFieldKey(article, absoluteIndex) {
      const key = article && article.key ? String(article.key) : `index-${absoluteIndex}`
      return `article:${key}`
    },
    grammarFieldCurrentText(fieldKey) {
      if (fieldKey === 'summary') {
        return String(this.form.summary || '')
      }

      if (!String(fieldKey || '').startsWith('article:')) return ''

      const article = this.resolveArticleByGrammarFieldKey(fieldKey)
      return String(article && article.content ? article.content : '')
    },
    grammarFieldLabel(fieldKey) {
      if (fieldKey === 'summary') {
        return 'Additional Note'
      }

      if (!String(fieldKey || '').startsWith('article:')) {
        return 'Grammar Assistant'
      }

      const article = this.resolveArticleByGrammarFieldKey(fieldKey)
      const articleTitle = String(article && article.title ? article.title : '').trim()
      if (articleTitle) return articleTitle

      const articleIndex = (this.form.articleDrafts || []).findIndex((item) => item === article)
      return articleIndex >= 0 ? `Article ${articleIndex + 1}` : 'Article Content'
    },
    grammarFieldContextValues(fieldKey) {
      if (fieldKey === 'summary') {
        return ['Additional Note']
      }

      return [this.grammarFieldLabel(fieldKey), 'Article Content']
    },
    resolveArticleByGrammarFieldKey(fieldKey) {
      const key = String(fieldKey || '').replace(/^article:/, '')
      if (!key) return null

      if (key.startsWith('index-')) {
        const index = Number(key.slice(6))
        return Number.isInteger(index) ? (this.form.articleDrafts[index] || null) : null
      }

      return (this.form.articleDrafts || []).find((item) => String(item && item.key ? item.key : '') === key) || null
    },
    buildGrammarContextValues(extraValues) {
      return [
        this.form.title,
        this.form.partner,
        this.form.contactPerson,
        this.form.department,
        this.selectedCountryText
      ].concat(Array.isArray(extraValues) ? extraValues : [])
    },
    grammarButtonDisabled(fieldKey, sourceText) {
      const state = this.grammarFieldState(fieldKey)
      return state.status === 'loading' || state.status === 'applying'
    },
    grammarChipTone(fieldKey, sourceText) {
      const normalizedText = String(sourceText || '').trim()
      const state = this.grammarFieldState(fieldKey)

      if (!normalizedText) return 'idle'
      if (state.status === 'loading' || state.status === 'applying') return 'pending'
      if (state.error) return 'error'
      if (this.isGrammarFieldStale(fieldKey, sourceText)) return 'stale'
      if (state.checkedAt && state.results.length === 0) return 'success'
      if (state.checkedAt && state.results.length > 0) return 'results'
      return 'idle'
    },
    grammarChipLabel(fieldKey, sourceText) {
      const tone = this.grammarChipTone(fieldKey, sourceText)

      if (tone === 'pending') return 'Checking'
      if (tone === 'error') return 'Try Again'
      if (tone === 'stale') return 'Refresh'
      if (tone === 'success') return 'Looks Good'
      if (tone === 'results') return 'Review'
      return 'Grammar Assist'
    },
    grammarChipBadge(fieldKey, sourceText) {
      const tone = this.grammarChipTone(fieldKey, sourceText)
      const state = this.grammarFieldState(fieldKey)

      if (tone === 'results') return String(state.results.length)
      if (tone === 'success') return 'OK'
      if (tone === 'stale') return 'New'
      return ''
    },
    isGrammarFieldStale(fieldKey, sourceText) {
      const state = this.grammarFieldState(fieldKey)
      return !!state.checkedAt && state.checkedText !== String(sourceText || '')
    },
    grammarPanelTone(fieldKey, sourceText) {
      const state = this.grammarFieldState(fieldKey)
      if (state.error) return 'error'
      if (state.status === 'loading' || state.status === 'applying') return 'pending'
      if (this.isGrammarFieldStale(fieldKey, sourceText)) return 'stale'
      if (state.checkedAt && state.results.length === 0) return 'success'
      return 'results'
    },
    grammarPanelTitle(fieldKey, sourceText) {
      const state = this.grammarFieldState(fieldKey)
      if (state.status === 'loading') return 'Checking grammar...'
      if (state.status === 'applying') return 'Applying suggestion...'
      if (state.error) return 'Grammar Assistant is unavailable'
      if (this.isGrammarFieldStale(fieldKey, sourceText)) return 'Suggestions need a refresh'
      if (state.checkedAt && state.results.length === 0) return 'No issues found'
      return `${state.results.length} suggestion${state.results.length === 1 ? '' : 's'} found`
    },
    grammarPanelCopy(fieldKey, sourceText) {
      const state = this.grammarFieldState(fieldKey)
      if (state.status === 'loading') return 'The first run can take a little longer while Harper warms up in your browser.'
      if (state.status === 'applying') return 'Updating the draft text and refreshing the latest suggestions.'
      if (state.error) return 'You can keep drafting and save normally while the assistant is unavailable.'
      if (this.isGrammarFieldStale(fieldKey, sourceText)) return 'The text changed after the previous check, so older suggestions may no longer match.'
      if (state.checkedAt && state.results.length === 0) return 'The latest pass did not flag any grammar or spelling issues in this field.'
      return 'Review each suggestion before applying it to the agreement text.'
    },
    serializeGrammarIssue(lint, index) {
      const span = lint && lint.span ? lint.span() : { start: 0, end: 0 }
      const suggestions = lint && lint.suggestions ? lint.suggestions() : []

      return {
        id: `lint-${index}-${span.start}-${span.end}`,
        lint,
        category: lint && lint.lint_kind_pretty ? lint.lint_kind_pretty() : 'Grammar',
        message: lint && lint.message ? lint.message() : 'Suggestion available.',
        problemText: lint && lint.get_problem_text ? lint.get_problem_text() : '',
        suggestions: suggestions.map((suggestion, suggestionIndex) => ({
          id: `suggestion-${index}-${suggestionIndex}`,
          suggestion,
          actionLabel: buildSuggestionActionLabel(suggestion, suggestionIndex)
        }))
      }
    },
    async openGrammarWorkspace(fieldKey) {
      this.activeGrammarFieldKey = fieldKey
      this.grammarWorkspaceVisible = true

      const sourceText = this.grammarFieldCurrentText(fieldKey)
      const state = this.grammarFieldState(fieldKey)
      if (!String(sourceText || '').trim()) return
      if (state.status === 'loading' || state.status === 'applying') return
      if (state.checkedAt && !this.isGrammarFieldStale(fieldKey, sourceText)) return

      await this.runGrammarCheck(fieldKey, sourceText, this.grammarFieldContextValues(fieldKey))
    },
    closeGrammarWorkspace() {
      this.grammarWorkspaceVisible = false
    },
    refreshActiveGrammarWorkspace() {
      if (!this.activeGrammarFieldKey) return Promise.resolve()
      const sourceText = this.grammarFieldCurrentText(this.activeGrammarFieldKey)
      if (!String(sourceText || '').trim()) return Promise.resolve()

      return this.runGrammarCheck(
        this.activeGrammarFieldKey,
        sourceText,
        this.grammarFieldContextValues(this.activeGrammarFieldKey)
      )
    },
    async runGrammarCheck(fieldKey, sourceText, extraContextValues) {
      const state = this.ensureGrammarFieldState(fieldKey)
      const normalizedText = String(sourceText || '')

      if (!normalizedText.trim()) {
        state.status = 'error'
        state.error = 'Add some English text before running Grammar Assistant.'
        state.results = []
        state.checkedAt = ''
        state.checkedText = ''
        return
      }

      state.status = 'loading'
      state.error = ''
      state.results = []

      try {
        const lints = await lintHarperGrammar(normalizedText, {
          contextValues: this.buildGrammarContextValues(extraContextValues)
        })

        state.status = 'ready'
        state.error = ''
        state.results = lints.map((lint, index) => this.serializeGrammarIssue(lint, index))
        state.checkedAt = new Date().toISOString()
        state.checkedText = normalizedText
      } catch (error) {
        state.status = 'error'
        state.error = formatGrammarError(error)
        state.results = []
        state.checkedAt = new Date().toISOString()
        state.checkedText = normalizedText
      }
    },
    runSummaryGrammarCheck() {
      return this.openGrammarWorkspace('summary')
    },
    runArticleGrammarCheck(article, absoluteIndex) {
      return this.openGrammarWorkspace(this.articleGrammarFieldKey(article, absoluteIndex))
    },
    updateGrammarFieldText(fieldKey, nextText) {
      if (fieldKey === 'summary') {
        this.form.summary = nextText
        return
      }

      if (!String(fieldKey || '').startsWith('article:')) return

      const article = this.resolveArticleByGrammarFieldKey(fieldKey)
      if (article) {
        article.content = nextText
      }
    },
    async applyGrammarSuggestion(fieldKey, sourceText, issue, suggestion, extraContextValues) {
      const state = this.ensureGrammarFieldState(fieldKey)
      state.status = 'applying'
      state.error = ''

      try {
        const nextText = await applyHarperGrammarSuggestion(String(sourceText || ''), issue.lint, suggestion.suggestion)
        this.updateGrammarFieldText(fieldKey, nextText)
        await this.runGrammarCheck(fieldKey, nextText, extraContextValues)
      } catch (error) {
        state.status = 'error'
        state.error = formatGrammarError(error)
      }
    },
    applyActiveGrammarSuggestion(issue, suggestion) {
      if (!this.activeGrammarFieldKey) return Promise.resolve()

      return this.applyGrammarSuggestion(
        this.activeGrammarFieldKey,
        this.grammarFieldCurrentText(this.activeGrammarFieldKey),
        issue,
        suggestion,
        this.grammarFieldContextValues(this.activeGrammarFieldKey)
      )
    },
    editorRouteFor(type, id) {
      const segment = {
        'Exchange Student': 'exchange',
        'Double Degree': 'double-degree',
        'Extension Agreement': 'extension',
        'MOU File': 'mou'
      }[type] || 'exchange'
      return id
        ? `/dashboard/user-agreements/${id}/${segment}`
        : `/dashboard/user-agreements/new/${segment}`
    },
    defaultForm() {
      return {
        title: '',
        partner: '',
        country: [],
        partnerEmail: '',
        contactPerson: '',
        department: 'International Affairs',
        summary: '',
        activityType: this.agreementType,
        draftSource: 'builder',
        templateSelections: [],
        templateFields: {
          scope: '',
          duration: '',
          language: 'English',
          terms: ''
        },
        articleDrafts: [],
        customDocument: {
          title: '',
          content: ''
        },
        uploadedDraft: null,
        partnerLogo: null
      }
    },
    normalizeClauseItem(item) {
      return {
        id: normalizeClauseId(item),
        title: String(item && item.title ? item.title : 'Clause'),
        content: String(item && item.content ? item.content : ''),
        category: String(item && item.category ? item.category : 'General'),
        sourceType: String(item && item.sourceType ? item.sourceType : 'default'),
        appliesTo: Array.isArray(item && item.appliesTo) ? item.appliesTo.map((entry) => String(entry)) : [],
        editable: !!(item && item.editable)
      }
    },
    normalizeAppliesToLabel(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .sort()
        .join(' ')
    },
    clauseAppliesToAgreement(item) {
      const appliesTo = Array.isArray(item && item.appliesTo) ? item.appliesTo : []
      if (!appliesTo.length) return true
      const activityType = String(this.agreementType || '').trim().toLowerCase()
      const normalizedActivityType = this.normalizeAppliesToLabel(this.agreementType)
      return appliesTo.some((entry) => {
        const rawEntry = String(entry || '').trim().toLowerCase()
        const normalizedEntry = this.normalizeAppliesToLabel(entry)
        return rawEntry === activityType
          || rawEntry.includes(activityType)
          || activityType.includes(rawEntry)
          || normalizedEntry === normalizedActivityType
      })
    },
    isSuggestedClause(clauseId) {
      return this.suggestedClauseIds.includes(String(clauseId))
    },
    getClauseArticleId(article) {
      return parseClauseArticleId(article && article.key ? article.key : '')
    },
    isClauseArticle(article) {
      return !!this.getClauseArticleId(article)
    },
    buildClauseArticleDraft(clauseId, index, existingDraft) {
      const id = String(clauseId || '').trim()
      if (!id) return existingDraft ? { ...existingDraft } : null

      const clause = (Array.isArray(this.availableClauses) ? this.availableClauses : []).find((item) => String(item.id) === id)
      if (!(clause || existingDraft)) return null

      return {
        key: buildClauseArticleKey(id),
        title: existingDraft && String(existingDraft.title || '').trim()
          ? String(existingDraft.title)
          : String(clause && clause.title ? clause.title : `Clause ${index + 1}`),
        content: existingDraft && typeof existingDraft.content !== 'undefined'
          ? String(existingDraft.content)
          : String(clause && clause.content ? clause.content : '')
      }
    },
    syncSelectedClauseArticles(drafts) {
      const selectedIds = Array.isArray(this.selectedClauseIds)
        ? this.selectedClauseIds.map((item) => String(item || '').trim()).filter(Boolean)
        : []
      const selectedSet = new Set(selectedIds)
      const selectedIndexMap = selectedIds.reduce((map, id, index) => {
        map[id] = index
        return map
      }, {})
      const syncedDrafts = []
      const embeddedClauseIds = new Set()

      ;(Array.isArray(drafts) ? drafts : []).forEach((item) => {
        const nextDraft = {
          ...item
        }
        const clauseId = this.getClauseArticleId(nextDraft)
        if (!clauseId) {
          syncedDrafts.push(nextDraft)
          return
        }
        if (!selectedSet.has(clauseId)) return

        const clauseDraft = this.buildClauseArticleDraft(clauseId, selectedIndexMap[clauseId] || 0, nextDraft)
        if (!clauseDraft) return

        embeddedClauseIds.add(clauseId)
        syncedDrafts.push(clauseDraft)
      })

      selectedIds.forEach((clauseId, index) => {
        if (embeddedClauseIds.has(clauseId)) return
        const clauseDraft = this.buildClauseArticleDraft(clauseId, index)
        if (!clauseDraft) return
        syncedDrafts.push(clauseDraft)
      })

      return syncedDrafts
    },
    applySelectedClauseArticles() {
      this.form.articleDrafts = this.syncSelectedClauseArticles(this.form.articleDrafts)
      if (this.articlePage > this.totalArticlePages) {
        this.articlePage = this.totalArticlePages
      }
    },
    getArticlePageForIndex(index) {
      const safeIndex = Math.max(0, Number(index) || 0)
      return Math.floor(safeIndex / this.articlesPerPage) + 1
    },
    summarizeArticleForMove(item, fallbackIndex) {
      const rawTitle = String(item && item.title ? item.title : `Article ${fallbackIndex + 1}`).trim()
      return rawTitle.length > 34 ? `${rawTitle.slice(0, 34).trim()}...` : rawTitle
    },
    articleMoveDestinationOptions() {
      return (Array.isArray(this.form.articleDrafts) ? this.form.articleDrafts : []).map((item, index) => ({
        value: index,
        label: `#${index + 1} · P${this.getArticlePageForIndex(index)} · ${this.summarizeArticleForMove(item, index)}`
      }))
    },
    toggleArticleMovePanel(index) {
      if (this.isViewMode || this.form.articleDrafts.length <= 1) return
      this.activeMoveArticleIndex = this.activeMoveArticleIndex === index ? null : index
    },
    closeArticleMovePanel() {
      this.activeMoveArticleIndex = null
    },
    moveArticle(sourceIndex, target) {
      if (this.isViewMode || this.form.articleDrafts.length <= 1) return

      const drafts = Array.isArray(this.form.articleDrafts) ? this.form.articleDrafts.slice() : []
      const safeSource = Number(sourceIndex)
      const maxIndex = drafts.length - 1
      if (!Number.isInteger(safeSource) || safeSource < 0 || safeSource > maxIndex) return

      const rawTarget = target && target.target ? target.target.value : target
      let safeTarget = Number(rawTarget)
      if (!Number.isInteger(safeTarget)) return
      safeTarget = Math.max(0, Math.min(safeTarget, maxIndex))

      if (safeSource === safeTarget) {
        this.articlePage = this.getArticlePageForIndex(safeTarget)
        this.closeArticleMovePanel()
        return
      }

      const [movedArticle] = drafts.splice(safeSource, 1)
      drafts.splice(safeTarget, 0, movedArticle)
      this.form.articleDrafts = drafts
      this.articlePage = this.getArticlePageForIndex(safeTarget)
      this.closeArticleMovePanel()
    },
    async loadClauseOptions(forceRefresh) {
      if (!this.agreementId) return

      try {
        const response = await Service.agreements('clauses', { id: this.agreementId }, { forceRefresh: !!forceRefresh })
        const data = response && response.data && response.data.data ? response.data.data : {}
        const selectedItems = Array.isArray(data.selectedClauses)
          ? data.selectedClauses.map((item) => this.normalizeClauseItem(item)).filter((item) => item.id)
          : []
        const selectedSet = new Set(selectedItems.map((item) => item.id))
        const baseItems = Array.isArray(data.clauses)
          ? data.clauses
              .map((item) => this.normalizeClauseItem(item))
              .filter((item) => item.id && (this.clauseAppliesToAgreement(item) || selectedSet.has(item.id)))
          : []
        const merged = []
        const seen = new Set()

        ;[...selectedItems, ...baseItems].forEach((item) => {
          if (!item.id || seen.has(item.id)) return
          seen.add(item.id)
          merged.push(item)
        })

        this.availableClauses = merged
        this.suggestedClauseIds = Array.isArray(data.suggestedClauseIds)
          ? data.suggestedClauseIds.map((item) => String(item)).filter(Boolean)
          : []

        if (selectedItems.length || !this.selectedClauseIds.length) {
          this.selectedClauseIds = selectedItems.map((item) => item.id)
        }

        this.applySelectedClauseArticles()

        if (!this.clauseCategoryOptions.find((item) => item.value === this.activeClauseCategory)) {
          this.activeClauseCategory = 'all'
        }
      } catch (error) {
        if (!this.error) {
          this.error = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
            ? error.response.data.data.message
            : 'Clause library could not be loaded right now.'
        }
      }
    },
    toggleClauseSelection(clauseId, shouldSelect) {
      const id = String(clauseId || '').trim()
      if (!id || this.isViewMode) return

      if (shouldSelect) {
        if (!this.selectedClauseIds.includes(id)) {
          this.selectedClauseIds = this.selectedClauseIds.concat([id])
        }
      } else {
        this.selectedClauseIds = this.selectedClauseIds.filter((item) => String(item) !== id)
      }

      this.applySelectedClauseArticles()
    },
    shouldShowExpandButton(content) {
      return String(content || '').length > 200
    },
    toggleClauseExpand(clauseId) {
      const id = String(clauseId || '').trim()
      if (!id) return
      
      const index = this.expandedClauseIds.indexOf(id)
      if (index > -1) {
        this.expandedClauseIds.splice(index, 1)
      } else {
        this.expandedClauseIds.push(id)
      }
    },
    selectAllVisibleClauses() {
      if (this.isViewMode) return
      const visibleIds = this.filteredClauseOptions.map(item => String(item.id))
      const newSelected = [...new Set([...this.selectedClauseIds, ...visibleIds])]
      this.selectedClauseIds = newSelected
      this.applySelectedClauseArticles()
    },
    clearAllSelectedClauses() {
      if (this.isViewMode) return
      this.selectedClauseIds = []
      this.applySelectedClauseArticles()
    },
    async loadAgreement() {
      this.error = ''
      this.grammarFields = {}
      this.grammarWorkspaceVisible = false
      this.activeGrammarFieldKey = ''
      try {
        if (!this.agreementId) {
          this.form = this.defaultForm()
          this.form.articleDrafts = this.createDefaultArticleDrafts(this.form)
          this.articlePage = 1
          await this.createInitialDraft()
          return
        }
        const response = await Service.agreements('detail', { id: this.agreementId })
        const item = response && response.data && response.data.data ? response.data.data : null
        if (!item) return
        const nextForm = this.defaultForm()
        this.form = {
          ...nextForm,
          title: item.title || '',
          partner: item.partner || '',
          country: this.normalizeCountrySelection(item.country || item.countries || []),
          partnerEmail: item.partnerEmail || '',
          contactPerson: item.contactPerson || '',
          department: item.department || 'International Affairs',
          summary: item.summary || '',
          activityType: item.activityType || this.agreementType,
          draftSource: item.draftSource === 'upload' ? 'upload' : 'builder',
          templateSelections: Array.isArray(item.templateSelections) ? item.templateSelections : [],
          templateFields: Object.assign({}, nextForm.templateFields, item.templateFields || {}),
          articleDrafts: this.normalizeArticleDrafts(item.articleDrafts),
          customDocument: Object.assign({}, nextForm.customDocument, item.customDocument || {}),
          uploadedDraft: item.uploadedDraft || null,
          partnerLogo: item.partnerLogo || null
        }
        this.selectedClauseIds = Array.isArray(item.selectedClauseIds) && item.selectedClauseIds.length
          ? item.selectedClauseIds.map((entry) => String(entry))
          : (Array.isArray(item.selectedClauses) ? item.selectedClauses.map((entry) => normalizeClauseId(entry)).filter(Boolean) : [])

        this.builderDraftCache = this.form.draftSource === 'builder'
          ? this.cloneArticleDrafts(this.form.articleDrafts)
          : []
        this.importedDraftCache = this.form.draftSource === 'upload'
          ? this.cloneArticleDrafts(this.form.articleDrafts)
          : []

        if (!this.form.articleDrafts.length && this.form.draftSource !== 'upload') {
          this.form.articleDrafts = this.createDefaultArticleDrafts(this.form)
          this.builderDraftCache = this.cloneArticleDrafts(this.form.articleDrafts)
        }
        this.articlePage = 1
        await this.loadClauseOptions()
      } catch (error) {
        this.error = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : 'The agreement draft could not be loaded right now.'
      }
    },
    createDefaultArticleDrafts(baseForm) {
      const templateForm = this.buildTemplateForm(baseForm)
      const exactTemplateBuilder = EXACT_ARTICLE_TEMPLATES[this.agreementType]
      if (typeof exactTemplateBuilder === 'function') {
        return this.normalizeArticleDrafts(exactTemplateBuilder(templateForm))
      }
      return this.normalizeArticleDrafts(this.templateSections.map((section) => {
        const builder = TEMPLATE_COPY_MAP[section.key]
        return {
          key: section.key,
          title: section.label,
          content: typeof builder === 'function' ? builder(templateForm) : section.description
        }
      }))
    },
    normalizeCountrySelection(value) {
      const rawValues = Array.isArray(value)
        ? value
        : String(value || '')
            .split(',')
      return [...new Set(rawValues
        .map((item) => String(item || '').trim())
        .filter(Boolean))]
    },
    formatCountryList(value) {
      return this.normalizeCountrySelection(value).join(', ')
    },
    buildTemplateForm(baseForm) {
      return Object.assign({}, baseForm || {}, {
        country: this.formatCountryList(baseForm && baseForm.country)
      })
    },
    cloneArticleDrafts(drafts) {
      return Array.isArray(drafts)
        ? drafts.map(item => ({
            ...item
          }))
        : []
    },
    setDraftSource(nextSource) {
      if (this.isViewMode || nextSource === this.form.draftSource) return

      if (nextSource === 'upload') {
        this.builderDraftCache = this.cloneArticleDrafts(this.form.articleDrafts)
        this.form.draftSource = 'upload'
        const importedDrafts = this.cloneArticleDrafts(this.importedDraftCache)
        if (importedDrafts.length) {
          this.form.articleDrafts = importedDrafts
        } else if (!(this.form.uploadedDraft && this.form.uploadedDraft.name)) {
          this.form.articleDrafts = []
        }
        this.applySelectedClauseArticles()
        this.articlePage = 1
        return
      }

      if (this.form.draftSource === 'upload') {
        this.importedDraftCache = this.cloneArticleDrafts(this.form.articleDrafts)
      }
      this.form.draftSource = 'builder'
      const cachedDrafts = this.cloneArticleDrafts(this.builderDraftCache)
      this.form.articleDrafts = cachedDrafts.length ? cachedDrafts : this.createDefaultArticleDrafts(this.form)
      this.applySelectedClauseArticles()
      this.articlePage = 1
    },
    async createInitialDraft() {
      if (this.isBootstrappingDraft) return
      this.isBootstrappingDraft = true
      this.error = ''
      try {
        const response = await Service.agreements('create-draft', {
          title: this.form.title,
          partner: this.form.partner,
          country: this.selectedCountryText,
          partnerEmail: this.form.partnerEmail,
          contactPerson: this.form.contactPerson,
          department: this.form.department,
          summary: this.form.summary,
          activityType: this.agreementType,
          draftSource: this.form.draftSource,
          templateSelections: this.form.templateSelections,
          templateFields: this.form.templateFields,
          articleDrafts: this.form.articleDrafts,
          selectedClauses: this.selectedClauseIds,
          customDocument: this.form.customDocument,
          uploadedDraft: this.form.uploadedDraft,
          partnerLogo: this.form.partnerLogo
        })
        const item = response && response.data && response.data.data ? response.data.data : null
        if (item && item.id) {
          await this.$router.replace(this.editorRouteFor(this.agreementType, item.id))
        }
      } catch (error) {
        this.error = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : 'The initial agreement draft could not be created.'
      } finally {
        this.isBootstrappingDraft = false
      }
    },
    buildArticlePreviewSections() {
      return Array.isArray(this.form.articleDrafts)
        ? this.normalizeArticleDrafts(this.form.articleDrafts)
            .filter(item => String(item && (item.title || item.content || '')).trim())
            .map((item, index) => ({
              key: item.key || `article-${index}`,
              label: getAgreementSectionDisplayTitle(item),
              content: item.content || ''
            }))
        : []
    },
    buildSelectedClausePreviewSections() {
      const embeddedClauseIds = new Set(
        (Array.isArray(this.form.articleDrafts) ? this.form.articleDrafts : [])
          .map((item) => this.getClauseArticleId(item))
          .filter(Boolean)
      )
      const clauseMap = new Map((Array.isArray(this.availableClauses) ? this.availableClauses : []).map((item) => [item.id, item]))
      return this.selectedClauseIds
        .map((id, index) => {
          if (embeddedClauseIds.has(String(id))) return null
          const item = clauseMap.get(String(id))
          if (!item) return null
          return {
            key: `selected-clause-${index + 1}-${item.id}`,
            label: getAgreementSectionDisplayTitle(item) || item.title || `Clause ${index + 1}`,
            content: item.content || ''
          }
        })
        .filter((item) => item && String(item.content || '').trim())
    },
    buildDocumentPreviewSections() {
      return this.buildArticlePreviewSections().concat(this.buildSelectedClausePreviewSections())
    },
    articleChipLabel(article, absoluteIndex) {
      return buildAgreementArticleChipLabel(article, absoluteIndex)
    },
    isCenteredPreviewSection(section) {
      return isCenteredAgreementSection(section, this.agreementType)
    },
    isSignaturePreviewSection(section) {
      return isSignatureAgreementSection(section)
    },
    normalizeArticleDrafts(drafts) {
      return normalizeAgreementSections(drafts)
    },
    resetArticlesFromTemplate() {
      this.form.articleDrafts = this.createDefaultArticleDrafts(this.form)
      this.applySelectedClauseArticles()
      this.closeArticleMovePanel()
      this.articlePage = 1
    },
    addArticle() {
      this.form.articleDrafts.push({
        key: `custom-${Date.now()}`,
        title: `Additional Article ${this.form.articleDrafts.length + 1}`,
        content: ''
      })
      this.closeArticleMovePanel()
      this.articlePage = this.totalArticlePages
    },
    addSignatureBlock() {
      const partner = this.form.partner || 'Partner Institution'
      const contact = this.form.contactPerson || '(Name) ........................................'
      const countries = this.selectedCountryText
      this.form.articleDrafts.push({
        key: `signature-block-${Date.now()}`,
        title: 'Signature Block',
        content: `Mae Fah Luang University || ${[partner, countries].filter(Boolean).join(', ') || 'Partner Institution'}\nAsst. Prof. Matchima Naradisorn, Ph.D. || ${contact}\nPresident || (Position) ........................................\nDate: || Date:`
      })
      this.closeArticleMovePanel()
      this.articlePage = this.totalArticlePages
    },
    removeArticle(index) {
      const item = this.form.articleDrafts[index]
      const fieldKey = this.articleGrammarFieldKey(item, index)
      const clauseId = this.getClauseArticleId(item)
      if (clauseId) {
        this.selectedClauseIds = this.selectedClauseIds.filter((entry) => String(entry) !== clauseId)
        this.applySelectedClauseArticles()
        this.$delete(this.grammarFields, fieldKey)
        if (this.activeGrammarFieldKey === fieldKey) {
          this.activeGrammarFieldKey = ''
          this.grammarWorkspaceVisible = false
        }
        this.closeArticleMovePanel()
        return
      }
      this.form.articleDrafts.splice(index, 1)
      this.$delete(this.grammarFields, fieldKey)
      if (this.activeGrammarFieldKey === fieldKey) {
        this.activeGrammarFieldKey = ''
        this.grammarWorkspaceVisible = false
      }
      this.closeArticleMovePanel()
      if (this.articlePage > this.totalArticlePages) this.articlePage = this.totalArticlePages
    },
    startDragArticle(event, index) {
      if (this.isViewMode || this.form.articleDrafts.length <= 1) {
        event.preventDefault()
        return
      }
      this.closeArticleMovePanel()
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target)
    },
    dragOverArticle(event, index) {
      if (this.draggedIndex === null) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      this.dropTargetIndex = index
    },
    dragLeaveArticle() {
      this.dropTargetIndex = null
    },
    dropArticle(event, targetIndex) {
      event.preventDefault()
      
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) {
        this.draggedIndex = null
        this.dropTargetIndex = null
        return
      }

      this.closeArticleMovePanel()
      this.moveArticle(this.draggedIndex, targetIndex)
      this.draggedIndex = null
      this.dropTargetIndex = null
    },
    endDragArticle() {
      this.closeArticleMovePanel()
      this.draggedIndex = null
      this.dropTargetIndex = null
    },
    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const fileName = String(file.name || '').toLowerCase()
      const fileType = String(file.type || '').toLowerCase()
      const isWordDocument = fileName.endsWith('.doc')
        || fileName.endsWith('.docx')
        || fileType.includes('msword')
        || fileType.includes('wordprocessingml')

      if (!isWordDocument) {
        this.error = 'Only DOC and DOCX contract files can be imported.'
        this.success = ''
        if (event && event.target) event.target.value = ''
        return
      }

      const finishUpload = (url) => {
        this.error = ''
        this.form.uploadedDraft = {
          name: file.name,
          size: file.size,
          sizeLabel: `${Math.max(1, Math.round(file.size / 1024))} KB`,
          type: file.type || 'application/octet-stream',
          url: url || ''
        }
        this.form.draftSource = 'upload'
        this.form.articleDrafts = []
        this.importedDraftCache = []
        this.applySelectedClauseArticles()
        this.articlePage = 1
      }

      const dataReader = new FileReader()
      dataReader.onload = () => {
        const fileUrl = typeof dataReader.result === 'string' ? dataReader.result : ''
        finishUpload(fileUrl)
      }
      dataReader.readAsDataURL(file)
    },
    handlePartnerLogoUpload(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const allowedLogoTypes = ['image/png', 'image/jpeg']
      const fallbackName = String(file.name || '').toLowerCase()
      const isAllowedLogo = allowedLogoTypes.includes(file.type) || /\.(png|jpe?g)$/.test(fallbackName)
      if (!isAllowedLogo) {
        this.error = 'Partner logo must be a PNG, JPG, or JPEG file.'
        event.target.value = ''
        return
      }
      this.error = ''

      const reader = new FileReader()
      reader.onload = () => {
        this.form.partnerLogo = {
          name: file.name,
          type: file.type || 'image/png',
          url: typeof reader.result === 'string' ? reader.result : ''
        }
      }
      reader.readAsDataURL(file)
    },
    async saveDraft() {
      this.error = ''
      this.success = ''

      if (this.form.draftSource === 'upload' && !(this.form.uploadedDraft && this.form.uploadedDraft.name)) {
        this.error = 'Import a contract file before saving.'
        return null
      }

      if (this.form.draftSource !== 'upload' && !this.buildDocumentPreviewSections().length) {
        this.error = 'Add at least one article or embedded clause to the draft before saving.'
        return null
      }

      const payload = {
        title: this.form.title,
        partner: this.form.partner,
        country: this.selectedCountryText,
        partnerEmail: this.form.partnerEmail,
        contactPerson: this.form.contactPerson,
        department: this.form.department,
        summary: this.form.summary,
        activityType: this.agreementType,
        draftSource: this.form.draftSource,
        templateSelections: this.form.templateSelections,
        templateFields: this.form.templateFields,
        articleDrafts: this.form.articleDrafts,
        selectedClauses: this.selectedClauseIds,
        customDocument: this.form.customDocument,
        uploadedDraft: this.form.uploadedDraft,
        partnerLogo: this.form.partnerLogo
      }

      let response = null
      if (this.hasSavedDraft) {
        try {
          response = await Service.agreements('update-draft', {
            id: this.agreementId,
            ...payload
          })
        } catch (error) {
          this.error = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
            ? error.response.data.data.message
            : 'Draft could not be updated.'
          return null
        }
      } else {
        try {
          response = await Service.agreements('create-draft', payload)
        } catch (error) {
          this.error = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
            ? error.response.data.data.message
            : 'Draft could not be created.'
          return null
        }
      }

      const item = response && response.data && response.data.data ? response.data.data : null
      if (item) {
        this.form.articleDrafts = Array.isArray(item.articleDrafts) ? item.articleDrafts : this.form.articleDrafts
        this.form.customDocument = item.customDocument || this.form.customDocument
        this.form.uploadedDraft = item.uploadedDraft || this.form.uploadedDraft
        this.applySelectedClauseArticles()
      }
      if (item && item.id && !this.hasSavedDraft) {
        await this.$router.replace(this.editorRouteFor(this.agreementType, item.id))
      }
      this.success = 'Draft saved.'
      return item
    },
    goBack() {
      this.$router.push('/dashboard/user-agreements')
    },
    async goNext() {
      this.error = ''
      this.success = ''
      if (!this.hasSavedDraft) {
        this.error = 'Draft is still being prepared. Please wait a moment and try again.'
        return
      }
      const saved = await this.saveDraft()
      if (!saved) return
      this.$router.push(`/dashboard/user-agreements/${this.agreementId}/review-summary`)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-agreement-editor");

.back-link { background: transparent; border: 0; color: #8c1515; cursor: pointer; font-weight: 700; margin-bottom: 12px; padding: 0; }
.editor-shell { border-radius: var(--app-radius-xl); }
.page-header { align-items: center; display: flex; gap: 16px; justify-content: space-between; margin-bottom: 20px; }
.kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.page-header h2 { color: #344767; margin: 6px 0; }
.page-header p { color: #718096; margin: 0; max-width: 720px; }
.header-actions, .mode-tabs { display: flex; flex-wrap: wrap; gap: 10px; }
.mode-tabs { margin-bottom: 18px; }
.mode-tab { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 999px; cursor: pointer; font-weight: 700; padding: 10px 14px; }
.mode-tab.active { background: #8c1515; border-color: #8c1515; color: #fff; }
.notice { border-radius: 14px; margin-bottom: 16px; padding: 12px 14px; }
.notice--error { background: #fff1f2; color: #b42318; }
.notice--success { background: #ecfdf3; color: #027a48; }
.layout-grid { display: grid; gap: 20px; grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr); }
.editor-column, .preview-column { min-width: 0; }
.form-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 18px; }
.form-grid--inner { margin-bottom: 0; margin-top: 18px; }
.field { align-content: start; display: grid; gap: 8px; }
.field-heading {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}
.field-heading--compact {
  align-items: flex-start;
  flex-wrap: wrap;
}
.field span { color: #344767; font-weight: 700; }
.field > input,
.field > select {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  box-sizing: border-box;
  height: 48px;
  padding: 0 12px;
  width: 100%;
}
.field > textarea {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  box-sizing: border-box;
  min-height: 112px;
  padding: 12px;
  resize: vertical;
  width: 100%;
}
.field-help {
  color: #718096;
  font-size: 0.78rem;
  line-height: 1.4;
}
.country-picker {
  position: relative;
}
.country-picker__control {
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--app-border-strong);
  border-radius: var(--app-radius-md);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 48px;
  padding: 8px;
}
.country-picker__input {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  flex: 1 1 140px;
  min-width: 120px;
  outline: none;
  padding: 4px !important;
}
.country-picker__input::-webkit-search-decoration,
.country-picker__input::-webkit-search-cancel-button,
.country-picker__input::-webkit-search-results-button,
.country-picker__input::-webkit-search-results-decoration {
  display: none;
}
.field .country-picker__placeholder {
  color: #8a97a8;
  font-weight: 500 !important;
}
.country-picker__menu {
  background: #fff;
  border: 1px solid #dfe7f0;
  border-radius: 14px;
  box-shadow: 0 18px 36px rgba(44, 52, 71, 0.14);
  display: grid;
  gap: 4px;
  left: 0;
  margin-top: 6px;
  max-height: 260px;
  overflow: auto;
  padding: 8px;
  position: absolute;
  right: 0;
  z-index: 20;
}
.country-picker__option {
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: #344767;
  cursor: pointer;
  font-weight: 700;
  padding: 10px;
  text-align: left;
}
.country-picker__option:hover,
.country-picker__option:focus {
  background: #fff6ea;
  color: #8c1515;
  outline: none;
}
.country-picker__empty {
  color: #718096;
  padding: 10px;
}
.country-picker--disabled .country-picker__control {
  background: #f8fafc;
}
.field .country-chip {
  align-items: center;
  background: #fff3cd;
  border: 1px solid #f0d98c;
  border-radius: 999px;
  color: #745b13;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 6px;
  line-height: 1;
  min-height: 30px;
  padding: 6px 8px 6px 10px;
}
.country-chip__remove {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(116, 91, 19, 0.18);
  border-radius: 999px;
  color: #8c1515;
  cursor: pointer;
  display: inline-flex;
  height: 20px;
  justify-content: center;
  padding: 0;
  width: 20px;
}
.grammar-chip {
  align-items: center;
  background: rgba(255, 248, 235, 0.9);
  border: 1px solid #ebd3a6;
  border-radius: 999px;
  box-shadow: 0 8px 18px rgba(140, 21, 21, 0.08);
  color: #8c1515;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  padding: 7px 10px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease;
}
.grammar-chip:hover:not(:disabled),
.grammar-chip:focus:not(:disabled) {
  background: #fff2d7;
  border-color: #d9ab5c;
  box-shadow: 0 14px 28px rgba(140, 21, 21, 0.12);
  outline: none;
  transform: translateY(-1px);
}
.grammar-chip[aria-pressed='true'] {
  border-color: #8c1515;
  box-shadow: 0 16px 32px rgba(140, 21, 21, 0.16);
}
.grammar-chip:disabled {
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.88;
}
.grammar-chip__dot {
  background: currentColor;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
  height: 8px;
  opacity: 0.9;
  width: 8px;
}
.grammar-chip__label {
  font-size: 0.77rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.grammar-chip__badge {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  justify-content: center;
  min-width: 28px;
  padding: 3px 7px;
}
.grammar-chip--idle {
  background: rgba(248, 250, 252, 0.96);
  border-color: #d8e1eb;
  color: #52606d;
}
.grammar-chip--pending {
  background: #fff4df;
  border-color: #d9ab5c;
  color: #8c1515;
}
.grammar-chip--results {
  background: #fff0ef;
  border-color: #efc1ba;
  color: #9f1239;
}
.grammar-chip--success {
  background: #eefbf3;
  border-color: #b7ebc6;
  color: #027a48;
}
.grammar-chip--stale {
  background: #fff7ea;
  border-color: #efcf96;
  color: #9a6700;
}
.grammar-chip--error {
  background: #fff3f2;
  border-color: #f4c7c7;
  color: #b42318;
}
.grammar-workspace {
  bottom: 24px;
  pointer-events: none;
  position: fixed;
  right: 24px;
  top: 92px;
  width: min(390px, calc(100vw - 32px));
  z-index: 30;
}
.grammar-workspace__panel {
  background:
    radial-gradient(circle at top left, rgba(255, 245, 225, 0.92), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 252, 255, 0.98) 100%);
  border: 1px solid #eadfca;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(39, 44, 63, 0.18);
  display: grid;
  gap: 0;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  pointer-events: auto;
}
.grammar-workspace--pending .grammar-workspace__panel {
  border-color: #efd7ab;
}
.grammar-workspace--success .grammar-workspace__panel {
  border-color: #b7ebc6;
}
.grammar-workspace--error .grammar-workspace__panel {
  border-color: #f2c5c5;
}
.grammar-workspace--stale .grammar-workspace__panel {
  border-color: #efcf96;
}
.grammar-workspace__header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 18px 16px;
}
.grammar-workspace__body {
  display: grid;
  gap: 16px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 18px 18px;
  -webkit-overflow-scrolling: touch;
}
.grammar-workspace__intro {
  display: grid;
  gap: 6px;
}
.grammar-workspace__eyebrow {
  color: #8c1515;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.grammar-workspace__intro h3 {
  color: #27364a;
  font-size: 1.15rem;
  line-height: 1.2;
  margin: 0;
}
.grammar-workspace__intro p {
  color: #718096;
  font-size: 0.84rem;
  line-height: 1.55;
  margin: 0;
  max-width: 240px;
}
.grammar-workspace__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 108px;
}
.grammar-workspace__action,
.grammar-workspace__close {
  background: #fff;
  border: 1px solid #d8e1eb;
  border-radius: 999px;
  color: #52606d;
  cursor: pointer;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 8px 12px;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}
.grammar-workspace__action:hover:not(:disabled),
.grammar-workspace__action:focus:not(:disabled),
.grammar-workspace__close:hover,
.grammar-workspace__close:focus {
  background: #fff8eb;
  border-color: #d9ab5c;
  color: #8c1515;
  outline: none;
}
.grammar-workspace__action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.grammar-workspace__status {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #eef2f7;
  border-radius: 18px;
  display: grid;
  gap: 6px;
  padding: 14px 15px;
}
.grammar-workspace__status strong {
  color: #27364a;
  font-size: 0.92rem;
}
.grammar-workspace__status span {
  color: #718096;
  font-size: 0.82rem;
  line-height: 1.55;
}
.grammar-workspace__empty,
.grammar-workspace__loading,
.grammar-workspace__success {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #eef2f7;
  border-radius: 18px;
  color: #52606d;
  padding: 16px;
}
.grammar-workspace__empty {
  line-height: 1.6;
}
.grammar-workspace__loading {
  align-items: center;
  display: grid;
  gap: 12px;
  justify-items: flex-start;
}
.grammar-workspace__loading p {
  margin: 0;
  line-height: 1.6;
}
.grammar-workspace__pulse {
  animation: grammar-pulse 1.3s ease-in-out infinite;
  background: linear-gradient(90deg, #f6d79e 0%, #8c1515 100%);
  border-radius: 999px;
  height: 10px;
  width: 88px;
}
.grammar-workspace__success {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr);
}
.grammar-workspace__success-mark {
  align-items: center;
  background: #ecfdf3;
  border-radius: 16px;
  color: #027a48;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  height: 42px;
  justify-content: center;
  min-width: 42px;
}
.grammar-workspace__success strong {
  color: #166534;
  display: block;
  font-size: 0.9rem;
  margin-bottom: 4px;
}
.grammar-workspace__success p {
  color: #52606d;
  line-height: 1.55;
  margin: 0;
}
.grammar-workspace__panel .grammar-issues { padding-right: 2px; }
.grammar-message {
  border-radius: 12px;
  font-size: 0.84rem;
  line-height: 1.55;
  padding: 10px 12px;
}
.grammar-message--error {
  background: #fff1f2;
  color: #b42318;
}
.grammar-message--stale {
  background: #fff4df;
  color: #8c1515;
}
.grammar-issues {
  display: grid;
  gap: 12px;
}
.grammar-issue {
  background: rgba(255, 255, 255, 0.93);
  border: 1px solid #e9eef5;
  border-radius: 18px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  display: grid;
  gap: 10px;
  padding: 14px;
}
.grammar-issue__meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.grammar-issue__kind {
  background: #eef2ff;
  border-radius: 999px;
  color: #344767;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 8px;
}
.grammar-issue__problem {
  color: #8c1515;
  font-size: 0.78rem;
  font-weight: 700;
}
.grammar-issue__message {
  color: #52606d;
  font-size: 0.86rem;
  line-height: 1.55;
  margin: 0;
}
.grammar-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.grammar-suggestion-btn {
  background: #fff;
  border: 1px solid #8c1515;
  border-radius: 999px;
  color: #8c1515;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 7px 12px;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.grammar-suggestion-btn:hover:not(:disabled),
.grammar-suggestion-btn:focus:not(:disabled) {
  background: #8c1515;
  border-color: #8c1515;
  color: #fff;
  outline: none;
}
.grammar-suggestion-btn:disabled {
  border-color: #d6dee8;
  color: #a0aec0;
  cursor: not-allowed;
}
.grammar-workspace-fade-enter-active,
.grammar-workspace-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.grammar-workspace-fade-enter,
.grammar-workspace-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes grammar-pulse {
  0%, 100% { opacity: 0.5; transform: scaleX(0.96); }
  50% { opacity: 1; transform: scaleX(1); }
}
.logo-input-note {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dde4ec;
  border-radius: var(--app-radius-md);
  color: #718096;
  display: flex;
  min-height: 46px;
  padding: 0 12px;
}
.field--full { grid-column: 1 / -1; }
.content-card, .preview-card {
  background: #fbfcfe;
  border: 1px solid #e6ebf1;
  border-radius: var(--app-radius-lg);
  padding: 18px;
}
.content-card__header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}
.content-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.content-card__title, .preview-card__header h3 { color: #344767; font-weight: 700; margin: 0; }
.content-card__copy { color: #718096; margin: 10px 0 0; }
.article-list { display: grid; gap: 14px; margin-top: 18px; }
.article-pagination {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
  position: relative;
}
.article-pagination__label {
  color: #718096;
  font-size: 0.88rem;
  font-weight: 700;
}
.article-pagination__button {
  border-radius: 999px;
  font-weight: 700;
  min-width: 92px;
  padding-left: 16px;
  padding-right: 16px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.article-pagination__button:hover:not(:disabled),
.article-pagination__button:focus:not(:disabled) {
  transform: translateY(-1px);
}
.article-pagination__button--primary {
  background: linear-gradient(135deg, #f0b24b 0%, #d39b35 100%);
  border-color: #c48a20;
  box-shadow: 0 10px 20px rgba(211, 155, 53, 0.24);
  color: #5a3410;
}
.article-pagination__button--primary:hover:not(:disabled),
.article-pagination__button--primary:focus:not(:disabled),
.article-pagination__button--primary:active:not(:disabled) {
  background: linear-gradient(135deg, #e6a73a 0%, #c98d24 100%);
  border-color: #b97f17;
  color: #4f2c0c;
}
.article-pagination__button--secondary {
  background: #fffaf2;
  border-color: #dfbf8a;
  box-shadow: 0 8px 18px rgba(140, 21, 21, 0.08);
  color: #8c1515;
}
.article-pagination__button--secondary:hover:not(:disabled),
.article-pagination__button--secondary:focus:not(:disabled),
.article-pagination__button--secondary:active:not(:disabled) {
  background: #fff4e3;
  border-color: #d3a968;
  color: #7a1212;
}
.article-pagination__button:disabled {
  background: #f8fafc;
  border-color: #e2e8f0;
  box-shadow: none;
  color: #a0aec0;
}
.article-pagination__button--secondary {
  left: 0;
  position: absolute;
}
.article-pagination__button--primary {
  position: absolute;
  right: 0;
}
.article-card {
  background: #fff;
  border: 1px solid #e1e8f0;
  border-radius: 16px;
  padding: 16px;
  transition: all 0.2s ease;
}
.article-card[draggable="true"] {
  cursor: grab;
}
.article-card[draggable="true"]:active {
  cursor: grabbing;
}
.article-card--dragging {
  opacity: 0.5;
  border: 2px dashed #8c1515;
}
.article-card__meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
  margin-bottom: 14px;
}
.article-card__controls {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-left: auto;
}
.article-drag-handle {
  align-items: center;
  color: #cbd5e1;
  cursor: grab;
  display: flex;
  font-size: 0.8rem;
  margin-right: 8px;
  padding: 4px 6px;
  user-select: none;
}
.article-drag-handle:active {
  cursor: grabbing;
}
.drag-icon {
  font-weight: 700;
  letter-spacing: -2px;
}
.article-chip {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.35;
  max-width: 100%;
  padding: 6px 10px;
  white-space: normal;
}
.article-source-chip {
  background: #eef2ff;
  border-radius: 999px;
  color: #344767;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 10px;
}
.article-move-trigger {
  background: #fff;
  border: 1px solid #dfbf8a;
  border-radius: 999px;
  color: #8c1515;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 6px 12px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}
.article-move-trigger:hover,
.article-move-trigger:focus {
  background: #fff7ea;
  border-color: #d3a968;
  box-shadow: 0 8px 16px rgba(140, 21, 21, 0.08);
  outline: none;
  transform: translateY(-1px);
}
.article-move-trigger--active {
  background: #fff4df;
  border-color: #d39b35;
  box-shadow: 0 10px 20px rgba(211, 155, 53, 0.18);
}
.article-remove {
  background: transparent;
  border: 0;
  color: #b42318;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 700;
  padding: 0;
}
.article-move-panel {
  background: linear-gradient(180deg, #fffdf7 0%, #fff6e7 100%);
  border: 1px solid #ecd7ab;
  border-radius: 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
}
.article-move-panel__field {
  margin-bottom: 0;
}
.article-move-panel__field span {
  color: #7a1212;
  font-size: 0.78rem;
}
.article-move-panel__field select {
  background: #fff;
  min-height: 46px;
}
.article-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.article-action-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #52606d;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 6px 10px;
  transition: all 0.2s ease;
}
.article-action-btn:hover {
  background: #f1f5f9;
  border-color: #8c1515;
  color: #8c1515;
}
.article-action-btn--move-up {
  border-color: #cbd5e1;
  color: #52606d;
}
.article-action-btn--move-down {
  border-color: #cbd5e1;
  color: #52606d;
}
.upload-box { background: #fff; border: 1px dashed #cbd5e1; border-radius: 14px; padding: 18px; }
.upload-meta { color: #52606d; display: grid; gap: 4px; margin-top: 10px; }
.upload-box input[type="file"] { display: block; width: 100%; }
.clause-library-card { margin-top: 18px; }
.clause-selection-pill {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  padding: 8px 12px;
  white-space: nowrap;
}
.clause-library-tools {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) 220px;
  margin-top: 18px;
}
.clause-library-search,
.clause-library-filter {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 14px;
  min-height: 46px;
  padding: 0 14px;
}
.clause-option-list {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}
.clause-option-card {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #e1e8f0;
  border-radius: 16px;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 16px;
}
.clause-option-card input[type="checkbox"] {
  margin-top: 4px;
}
.clause-option-card__body {
  display: grid;
  gap: 10px;
}
.clause-option-card__header {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}
.clause-option-card__title {
  color: #344767;
  font-weight: 700;
}
.clause-option-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.clause-option-card__badge {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 5px 9px;
}
.clause-option-card__badge--muted {
  background: #f8fafc;
  color: #52606d;
}
.clause-option-card__badge--suggested {
  background: #ecfdf3;
  color: #027a48;
}
.clause-option-card__content {
  color: #52606d;
  line-height: 1.65;
  white-space: pre-wrap;
  position: relative;
}
.clause-option-card__content--collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
}
.clause-expand-toggle {
  background: transparent;
  border: none;
  color: #8c1515;
  cursor: pointer;
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 8px;
  padding: 0;
  text-decoration: underline;
}
.clause-expand-toggle:hover {
  opacity: 0.8;
}
.clause-option-card--selected {
  background: #fffaf2;
  border-color: #dfbf8a;
}
.clause-header-stats {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  align-items: center;
}
.clause-stats-text {
  align-items: center;
  background: #f1f5f9;
  border-radius: 999px;
  color: #52606d;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  padding: 6px 10px;
  white-space: nowrap;
}
.clause-quick-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}
.clause-quick-action-btn {
  background: #fff;
  border: 1px solid #8c1515;
  border-radius: 10px;
  color: #8c1515;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 14px;
  transition: all 0.2s ease;
  flex: 1;
}
.clause-quick-action-btn:hover:not(:disabled) {
  background: #8c1515;
  color: #fff;
}
.clause-quick-action-btn:disabled {
  border-color: #d6dee8;
  color: #a0aec0;
  cursor: not-allowed;
  opacity: 0.5;
}
.clause-quick-action-btn--secondary {
  background: #fff4df;
  border-color: #dfbf8a;
  color: #7a1212;
}
.clause-quick-action-btn--secondary:hover:not(:disabled) {
  background: #8c1515;
  border-color: #8c1515;
  color: #fff;
}
.clause-library-empty {
  align-items: center;
  background: #fff;
  border: 1px dashed #d6dee8;
  border-radius: 14px;
  color: #718096;
  display: flex;
  justify-content: center;
  margin-top: 18px;
  min-height: 120px;
  padding: 20px;
  text-align: center;
}
.preview-card { height: 100%; position: sticky; top: 24px; }
.preview-card__header { align-items: flex-start; display: flex; gap: 12px; justify-content: space-between; }
.preview-card__header > div { min-width: 0; }
.preview-card__document-header { margin-top: 18px; }
.preview-kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; margin-bottom: 6px; text-transform: uppercase; }
.preview-pill {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  flex: 0 0 auto;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.1;
  padding: 7px 10px;
  white-space: nowrap;
}
.agreement-header-preview {
  align-items: center;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(120px, 0.8fr) minmax(0, 1.2fr) minmax(120px, 0.8fr);
  margin-top: 18px;
  padding: 18px;
}
.logo-block {
  align-items: center;
  color: #52606d;
  display: grid;
  gap: 10px;
  justify-items: center;
  text-align: center;
}
.logo-block span {
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.4;
}
.agreement-header-title {
  align-items: center;
  display: grid;
  gap: 6px;
  justify-items: center;
  text-align: center;
}
.agreement-header-title__eyebrow {
  color: #8c1515;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.agreement-header-title strong {
  color: #344767;
  font-size: 1.1rem;
  line-height: 1.4;
}
.header-logo {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 16px;
  height: 72px;
  object-fit: contain;
  padding: 10px;
  width: 72px;
}
.header-logo--placeholder {
  align-items: center;
  color: #8c1515;
  display: flex;
  font-size: 1.1rem;
  font-weight: 800;
  justify-content: center;
}
.preview-meta { color: #718096; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.preview-meta span {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 999px;
  padding: 6px 10px;
}
.document-preview, .import-preview { margin-top: 18px; }
.document-file-card {
  align-items: center;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 14px 16px;
}
.document-file-card__title { color: #344767; font-weight: 700; }
.document-file-card__link { color: #8c1515; font-weight: 700; }
.preview-section + .preview-section { margin-top: 16px; }
.preview-section__label { color: #344767; font-size: 0.82rem; font-weight: 700; margin-bottom: 6px; text-transform: uppercase; }
.preview-section p { color: #52606d; line-height: 1.65; margin: 0; white-space: pre-wrap; }
.preview-section__content--centered { text-align: center; }
.preview-signature-columns {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.preview-signature-column {
  background: #fbfcfe;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 14px;
}
.preview-signature-line {
  color: #52606d;
  line-height: 1.6;
  white-space: pre-wrap;
}
.import-preview__info { display: grid; gap: 4px; margin-bottom: 14px; }
.import-preview__info strong { color: #344767; }
.import-preview__info span { color: #718096; }
.empty-preview {
  align-items: center;
  background: #fff;
  border: 1px dashed #d6dee8;
  border-radius: 14px;
  color: #718096;
  display: flex;
  justify-content: center;
  min-height: 180px;
  padding: 20px;
  text-align: center;
}
@media (max-width: 1100px) {
  .layout-grid { grid-template-columns: 1fr; }
  .preview-card { position: static; }
}
@media (max-width: 768px) {
  .page-header { align-items: flex-start; flex-direction: column; }
  .form-grid { grid-template-columns: 1fr; }
  .content-card__header { flex-direction: column; }
  .article-card__controls { margin-left: 0; width: 100%; }
  .clause-library-tools { grid-template-columns: 1fr; }
  .clause-option-card__header { flex-direction: column; }
  .clause-option-card__badges { justify-content: flex-start; }
  .article-pagination {
    justify-content: space-between;
    position: static;
  }
  .article-pagination__button--secondary,
  .article-pagination__button--primary {
    position: static;
  }
  .agreement-header-preview { grid-template-columns: 1fr; }
  .preview-signature-columns { grid-template-columns: 1fr; }
  .grammar-workspace {
    bottom: 16px;
    left: 16px;
    right: 16px;
    top: auto;
    width: auto;
  }
  .grammar-workspace__panel {
    border-radius: 20px;
    max-height: min(68vh, 560px);
  }
  .grammar-workspace__header {
    flex-direction: column;
  }
  .grammar-workspace__intro p {
    max-width: none;
  }
  .grammar-workspace__actions {
    flex-direction: row;
    min-width: 0;
    width: 100%;
  }
  .grammar-workspace__action,
  .grammar-workspace__close {
    flex: 1;
  }
}
</style>
